import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAdmin } from '../context/AdminContext';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useLanguage } from '../context/LanguageContext';
import RichTextEditor from '../components/RichTextEditor';
import 'flag-icons/css/flag-icons.min.css';
import { adminFetchInit } from '../utils/adminToken';

// Common countries list with their codes and names
const countries = [
  { code: 'W', name: 'World' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'España' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'DE', name: 'Deutschland' },
  { code: 'IT', name: 'Italia' },
  { code: 'PT', name: 'Portugal' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'BR', name: 'Brasil' },
  { code: 'MX', name: 'México' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CL', name: 'Chile' },
  { code: 'PE', name: 'Perú' },
  { code: 'JP', name: '日本' },
  { code: 'KR', name: '한국' },
  { code: 'CN', name: '中国' },
  { code: 'IN', name: 'India' },
  { code: 'RU', name: 'Россия' },
  { code: 'NL', name: 'Nederland' },
  { code: 'BE', name: 'België' },
  { code: 'CH', name: 'Schweiz' },
  { code: 'AT', name: 'Österreich' },
  { code: 'PH', name: 'Philippines' }
];

const Settings = () => {
  const { admin, setAdmin, isAuthenticated, isLoading } = useAdmin();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  
  // Helper function to check if field is multilingual (object/array)
  const isMultilingualField = (fieldValue) => {
    if (!fieldValue) return false;
    
    // Check if it's an array
    if (Array.isArray(fieldValue)) return true;
    
    // Check if it's an object
    if (typeof fieldValue === 'object' && fieldValue !== null) return true;
    
    // Check if it's a JSON string that parses to object/array
    if (typeof fieldValue === 'string') {
      try {
        const parsed = JSON.parse(fieldValue);
        return typeof parsed === 'object' && parsed !== null;
      } catch (e) {
        return false;
      }
    }
    
    return false;
  };
  
  // Helper function to get appropriate language value based on sidebar language selection
  const getLanguageValue = (fieldValue) => {
    if (!isMultilingualField(fieldValue)) {
      // If not multilingual, return the value directly
      return fieldValue || '';
    }
    
    let parsedValue = fieldValue;
    
    // Parse JSON string if needed
    if (typeof fieldValue === 'string') {
      try {
        parsedValue = JSON.parse(fieldValue);
      } catch (e) {
        return fieldValue || '';
      }
    }
    
    // Handle array case (return first element)
    if (Array.isArray(parsedValue)) {
      return parsedValue.length > 0 ? parsedValue[0] : '';
    }
    
    // Handle object case - get value based on sidebar language selection
    if (typeof parsedValue === 'object' && parsedValue !== null) {
      if (currentLanguage === 'en') {
        // US selected - get english value
        return parsedValue.english || '';
      } else if (currentLanguage === 'fr') {
        // FR selected - get french value
        return parsedValue.french || '';
      }
    }
    
    return '';
  };
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('information');
  const [settings, setSettings] = useState({
    information: {
      name: '',
      country: '',
      social: '', // Social media links (will be converted to array)
      subscriber: '',
      url: '',
      youtube: [''], // Array to hold multiple YouTube links
      about: '',
      message: '',
      profile: 'Streamer', // Add profile type (default: Streamer)
      member_counter: '', // Add member counter for Discord
      monthly_traffic: '' // Add monthly traffic for Website
    },
    password: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  // Social media tag states
  const [socialMediaTags, setSocialMediaTags] = useState([]);
  const [socialInput, setSocialInput] = useState('');
  const [showSocialSuggestions, setShowSocialSuggestions] = useState(false);
  const [selectedSocialIndex, setSelectedSocialIndex] = useState(0); // For keyboard navigation

  // Image update states - Multiple image support
  const [newImages, setNewImages] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [imageUploadStatus, setImageUploadStatus] = useState(null); // null, 'uploading', 'success', 'error'
  const [imageUploadMessage, setImageUploadMessage] = useState('');
  const [isUpdatingImage, setIsUpdatingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Gallery states
  const [allImages, setAllImages] = useState([]);
  const [currentProfileIndex, setCurrentProfileIndex] = useState(0);
  const [isRemovingImage, setIsRemovingImage] = useState(null); // track which image is being removed
  const [isRemovingLogo, setIsRemovingLogo] = useState(null); // track which logo is being removed
  const [imageViewMode, setImageViewMode] = useState('profile'); // 'profile' or 'logo'
  const [openMediaDropdown, setOpenMediaDropdown] = useState(false); // Media Gallery dropdown state
  const [logoImages, setLogoImages] = useState([]); // Separate logo images array
  
  // Logo upload states
  const [newLogoImages, setNewLogoImages] = useState([]);
  const [newLogoImagePreviews, setNewLogoImagePreviews] = useState([]);
  const [isUpdatingLogo, setIsUpdatingLogo] = useState(false);
  
  // Image popup states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Loading states
  const [isUpdatingInfo, setIsUpdatingInfo] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false); // ✅ Add profile update state

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Appeal modal state
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [appealMessage, setAppealMessage] = useState('');
  const [isSendingAppeal, setIsSendingAppeal] = useState(false);

  // Services section states
  const [services, setServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null); // Track which service is being edited
  const [newService, setNewService] = useState({
    title: '',
    url: '',
    image: null,
    imagePreview: null,
    cloudinaryUrl: null,
    cloudinaryPublicId: null,
    isUploading: false
  });

  // Language modal states
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [languageModalType, setLanguageModalType] = useState(''); // 'message' or 'about'
  const [languageForm, setLanguageForm] = useState({
    country: '',
    text: ''
  });

  // Password visibility state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Country dropdown states
  const [locationData, setLocationData] = useState({
    country: '',
    countryCode: null,
    flagClass: null
  });
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Social media platforms configuration (memoized to prevent re-renders)
  const socialPlatforms = useMemo(() => [
    { name: 'YouTube', icon: '▶️', color: 'bg-red-500', urlPattern: 'https://www.youtube.com/@', useAt: true },
    { name: 'TikTok', icon: '🎵', color: 'bg-black', urlPattern: 'https://www.tiktok.com/@', useAt: true },
    { name: 'Instagram', icon: '📷', color: 'bg-pink-500', urlPattern: 'https://www.instagram.com/', useAt: false },
    { name: 'X', icon: '𝕏', color: 'bg-black', urlPattern: 'https://x.com/', useAt: false },
    { name: 'Facebook', icon: '👤', color: 'bg-blue-600', urlPattern: 'https://www.facebook.com/', useAt: false },
    { name: 'Twitch', icon: '🎮', color: 'bg-purple-500', urlPattern: 'https://www.twitch.tv/', useAt: false },
    { name: 'Discord', icon: '💬', color: 'bg-indigo-500', urlPattern: 'https://discord.com/users/', useAt: false }
  ], []);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle social input change
  const handleSocialInputChange = (e) => {
    const value = e.target.value;
    setSocialInput(value);
    setShowSocialSuggestions(value.length > 0);
    setSelectedSocialIndex(0); // Reset selection when typing
  };

  // Handle keyboard navigation for social media dropdown
  const handleSocialKeyDown = (e) => {
    if (!showSocialSuggestions || socialInput.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSocialIndex(prev => 
        prev < socialPlatforms.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSocialIndex(prev => 
        prev > 0 ? prev - 1 : socialPlatforms.length - 1
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (socialPlatforms[selectedSocialIndex]) {
        addSocialTag(socialPlatforms[selectedSocialIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSocialSuggestions(false);
    }
  };

  // Add social media tag
  const addSocialTag = (platform) => {
    const username = socialInput.replace('@', '').trim();
    if (!username) return;

    const newTag = {
      platform: platform.name,
      username: username,
      icon: platform.icon,
      color: platform.color,
      useAt: platform.useAt
    };

    setSocialMediaTags(prev => [...prev, newTag]);
    setSocialInput('');
    setShowSocialSuggestions(false);

    // Update settings for submission
    updateSocialMediaSettings([...socialMediaTags, newTag]);
  };

  // Remove social media tag
  const removeSocialTag = (index) => {
    const newTags = socialMediaTags.filter((_, i) => i !== index);
    setSocialMediaTags(newTags);
    updateSocialMediaSettings(newTags);
  };

  // Convert tags to format needed for submission
  const updateSocialMediaSettings = (tags) => {
    const socialStrings = tags.map(tag => {
      const platform = socialPlatforms.find(p => p.name === tag.platform);
      return `${platform?.urlPattern}${tag.username}`;
    });
    
    setSettings(prev => ({
      ...prev,
      information: {
        ...prev.information,
        social: socialStrings.join(', ')
      }
    }));
  };

  // Get flag CSS class for country code
  const getFlagClass = (countryCode) => {
    if (!countryCode) {
      return null;
    }
    // Handle special case for World option
    if (countryCode === 'W') {
      return 'world-flag'; // Special class for world option
    }
    if (countryCode.length !== 2) {
      return null;
    }
    const code = countryCode.toLowerCase();
    return `fi fi-${code}`;
  };

  // Get country name from country object
  const getCountryName = useCallback((country) => {
    return country.name;
  }, []);

  // Handle location selection
  const handleLocationSelect = (country) => {
    const flagClass = getFlagClass(country.code);
    
    setShowLocationDropdown(false);
    setSearchQuery('');
    
    setLocationData({
      country: getCountryName(country),
      countryCode: country.code,
      flagClass: flagClass
    });

    // Update settings state
    handleSettingChange('information', 'country', getCountryName(country));
  };

  // Filter countries based on search query
  const filteredCountries = countries.filter(country => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const countryName = country.name.toLowerCase();
    const countryCode = country.code.toLowerCase();
    
    return countryName.includes(query) || countryCode.includes(query);
  });

  // Helper function to get all images from array
  const getAllImages = useCallback(() => {
    if (!admin?.image) return { profile: [], logo: [] };
    
    try {
      // Check if it's the new object format: {profile: [...], logo: [...]}
      if (typeof admin.image === 'string' && admin.image.startsWith('{')) {
        const imageData = JSON.parse(admin.image);
        return {
          profile: Array.isArray(imageData.profile) ? imageData.profile : [],
          logo: Array.isArray(imageData.logo) ? imageData.logo : []
        };
      }
      // If it's a JSON array string (old format), parse it
      else if (typeof admin.image === 'string' && admin.image.startsWith('[')) {
        const imageUrls = JSON.parse(admin.image);
        return {
          profile: Array.isArray(imageUrls) ? imageUrls : [],
          logo: []
        };
      }
      // If it's already an array (old format), return it
      else if (Array.isArray(admin.image)) {
        return {
          profile: admin.image,
          logo: []
        };
      }
      // If it's a single string URL (old format), make it an array
      else if (typeof admin.image === 'string') {
        return {
          profile: [admin.image],
          logo: []
        };
      }
    } catch (e) {
      console.log('⚠️ Error parsing image data:', e);
    }
    
    return { profile: [], logo: [] };
  }, [admin?.image]);

  // Set a specific image as the profile image (move to first position)
  const setAsProfileImage = async (imageIndex) => {
    if (imageIndex === 0) return; // Already the profile image
    
    try {
      const images = [...allImages];
      const selectedImage = images[imageIndex];
      
      // Remove the selected image from its current position
      images.splice(imageIndex, 1);
      // Add it to the beginning
      images.unshift(selectedImage);
      
      // Update database with new structure
      const updatedImageData = {
        profile: images,
        logo: logoImages // Maintain existing logo
      };
      
      const response = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          action: 'update',
          image: JSON.stringify(updatedImageData),
        }),
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile image');
      }

      const responseData = await response.json();
      
      // Update local state
      setAllImages(images);
      setCurrentProfileIndex(0);
      
      // Update admin state with new data from server
      if (responseData.admin) {
        setAdmin(responseData.admin);
      }
      
      showToastMessage(
        t('language') === 'fr'
          ? 'Image de profil mise à jour avec succès!'
          : 'Profile image updated successfully!',
        'success'
      );
    } catch (error) {
      console.error('Error setting profile image:', error);
      showToastMessage(
        t('language') === 'fr'
          ? 'Erreur lors de la mise à jour de l\'image de profil'
          : 'Error updating profile image',
        'error'
      );
    }
  };

  // Set a specific logo as the main logo (move to first position)
  const setAsMainLogo = async (logoIndex) => {
    if (logoIndex === 0) return; // Already the main logo
    
    try {
      const logos = [...logoImages];
      const selectedLogo = logos[logoIndex];
      
      // Remove the selected logo from its current position
      logos.splice(logoIndex, 1);
      // Add it to the beginning
      logos.unshift(selectedLogo);
      
      // Update database with new structure
      const updatedImageData = {
        profile: allImages, // Maintain existing profile
        logo: logos
      };
      
      const response = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          action: 'update',
          image: JSON.stringify(updatedImageData),
        }),
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update main logo');
      }

      const responseData = await response.json();
      
      // Update local state
      setLogoImages(logos);
      
      // Update admin state with new data from server
      if (responseData.admin) {
        setAdmin(responseData.admin);
      }
      
      showToastMessage(
        t('language') === 'fr'
          ? 'Logo principal mis à jour avec succès!'
          : 'Main logo updated successfully!',
        'success'
      );
    } catch (error) {
      console.error('Error setting main logo:', error);
      showToastMessage(
        t('language') === 'fr'
          ? 'Erreur lors de la mise à jour du logo principal'
          : 'Error updating main logo',
        'error'
      );
    }
  };

  // Remove an image from the gallery
  const removeImage = async (imageIndex) => {
    if (allImages.length <= 1) {
      showToastMessage(
        t('language') === 'fr'
          ? 'Vous devez avoir au moins une image'
          : 'You must have at least one image',
        'error'
      );
      return;
    }

    setIsRemovingImage(imageIndex);
    
    try {
      const images = [...allImages];
      const imageToDelete = images[imageIndex];
      
      // Check if this image is also used in the logo array
      const isUsedInLogo = logoImages.includes(imageToDelete);
      
      // Extract public_id from Cloudinary URL for deletion
      let publicId = null;
      try {
        const urlParts = imageToDelete.split('/');
        const uploadIndex = urlParts.indexOf('upload');
        if (uploadIndex !== -1 && uploadIndex < urlParts.length - 1) {
          // Skip version if present (v1234567890)
          let startIndex = uploadIndex + 1;
          if (urlParts[startIndex] && urlParts[startIndex].startsWith('v')) {
            startIndex += 1;
          }
          // Join the remaining parts and remove the file extension
          const pathParts = urlParts.slice(startIndex);
          publicId = pathParts.join('/').replace(/\.[^/.]+$/, '');
        }
      } catch (error) {
        console.warn('Could not extract public_id from image URL:', error);
      }

      // Delete from Cloudinary only if the image is NOT used in logo array
      if (publicId && !isUsedInLogo) {
        try {
          const deleteResponse = await fetch('/.netlify/functions/delete-cloudinary-image', adminFetchInit({
            method: 'POST',
            body: JSON.stringify({
              publicId: publicId,
            }),
          }));

          if (!deleteResponse.ok) {
            console.warn('Failed to delete image from Cloudinary, but continuing with database update');
          } else {
            console.log('✅ Image deleted from Cloudinary:', publicId);
          }
        } catch (cloudinaryError) {
          console.warn('Error deleting from Cloudinary:', cloudinaryError);
          // Continue with database update even if Cloudinary deletion fails
        }
      } else if (isUsedInLogo) {
        console.log('⚠️ Image not deleted from Cloudinary - still used in logo array');
      }

      // Remove from array
      images.splice(imageIndex, 1);
      
      // Update database with new structure
      const updatedImageData = {
        profile: images,
        logo: logoImages // Maintain existing logo
      };
      
      const response = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          action: 'update',
          image: JSON.stringify(updatedImageData),
        }),
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove image');
      }

      const responseData = await response.json();
      
      // Update local state
      setAllImages(images);
      
      // Update admin state with new data from server
      if (responseData.admin) {
        setAdmin(responseData.admin);
      }
      
      // Adjust profile index if necessary
      if (imageIndex === 0 && images.length > 0) {
        setCurrentProfileIndex(0); // New first image becomes profile
      } else if (currentProfileIndex > imageIndex) {
        setCurrentProfileIndex(currentProfileIndex - 1);
      }
      
      showToastMessage(
        t('language') === 'fr'
          ? 'Image supprimée avec succès!'
          : 'Image removed successfully!',
        'success'
      );
    } catch (error) {
      console.error('Error removing image:', error);
      showToastMessage(
        t('language') === 'fr'
          ? 'Erreur lors de la suppression de l\'image'
          : 'Error removing image',
        'error'
      );
    } finally {
      setIsRemovingImage(null);
    }
  };

  // Remove a logo from the logo gallery
  const removeLogoImage = async (logoIndex) => {
    if (logoImages.length <= 1) {
      showToastMessage(
        t('language') === 'fr'
          ? 'Vous devez avoir au moins un logo'
          : 'You must have at least one logo',
        'error'
      );
      return;
    }

    setIsRemovingLogo(logoIndex);
    
    try {
      const logos = [...logoImages];
      const logoToDelete = logos[logoIndex];
      
      // Check if this logo is also used in the profile array
      const isUsedInProfile = allImages.includes(logoToDelete);
      
      // Extract public_id from Cloudinary URL for deletion
      let publicId = null;
      try {
        const urlParts = logoToDelete.split('/');
        const uploadIndex = urlParts.indexOf('upload');
        if (uploadIndex !== -1 && uploadIndex < urlParts.length - 1) {
          // Skip version if present (v1234567890)
          let startIndex = uploadIndex + 1;
          if (urlParts[startIndex] && urlParts[startIndex].startsWith('v')) {
            startIndex += 1;
          }
          // Join the remaining parts and remove the file extension
          const pathParts = urlParts.slice(startIndex);
          publicId = pathParts.join('/').replace(/\.[^/.]+$/, '');
        }
      } catch (error) {
        console.warn('Could not extract public_id from logo URL:', error);
      }

      // Delete from Cloudinary only if the logo is NOT used in profile array
      if (publicId && !isUsedInProfile) {
        try {
          const deleteResponse = await fetch('/.netlify/functions/delete-cloudinary-image', adminFetchInit({
            method: 'POST',
            body: JSON.stringify({
              publicId: publicId,
            }),
          }));

          if (!deleteResponse.ok) {
            console.warn('Failed to delete logo from Cloudinary, but continuing with database update');
          } else {
            console.log('✅ Logo deleted from Cloudinary:', publicId);
          }
        } catch (cloudinaryError) {
          console.warn('Error deleting from Cloudinary:', cloudinaryError);
          // Continue with database update even if Cloudinary deletion fails
        }
      } else if (isUsedInProfile) {
        console.log('⚠️ Logo not deleted from Cloudinary - still used in profile array');
      }

      // Remove from array
      logos.splice(logoIndex, 1);
      
      // Update database with new structure
      const updatedImageData = {
        profile: allImages, // Maintain existing profile images
        logo: logos
      };
      
      const response = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          action: 'update',
          image: JSON.stringify(updatedImageData),
        }),
      }));

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove logo');
      }

      const responseData = await response.json();
      
      // Update local state
      setLogoImages(logos);
      
      // Update admin state with new data from server
      if (responseData.admin) {
        setAdmin(responseData.admin);
      }
      
      showToastMessage(
        t('language') === 'fr'
          ? 'Logo supprimé avec succès!'
          : 'Logo removed successfully!',
        'success'
      );
    } catch (error) {
      console.error('Error removing logo:', error);
      showToastMessage(
        t('language') === 'fr'
          ? 'Erreur lors de la suppression du logo'
          : 'Error removing logo',
        'error'
      );
    } finally {
      setIsRemovingLogo(null);
    }
  };

  // Image popup functions
  const handleImageClick = useCallback((imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  }, []);

  const closeImageModal = useCallback(() => {
    setShowImageModal(false);
    setSelectedImage(null);
  }, []);

  // Check authentication and redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Load current settings
  useEffect(() => {
    if (admin && isAuthenticated) {
      console.log('📊 Loading admin data:', admin);
      console.log('📊 Admin YouTube data:', admin.youtube);
      
      // Initialize with current admin data
      let youtubeLinks = [''];
      if (admin.youtube) {
        try {
          // Check if it's already an array
          if (Array.isArray(admin.youtube)) {
            if (admin.youtube.length > 0) {
              youtubeLinks = admin.youtube;
              console.log('✅ Using existing YouTube array:', youtubeLinks);
            }
          }
          // If it's a string, try to parse as JSON array
          else if (typeof admin.youtube === 'string') {
            if (admin.youtube.trim().startsWith('[')) {
              const parsed = JSON.parse(admin.youtube);
              if (Array.isArray(parsed) && parsed.length > 0) {
                youtubeLinks = parsed;
                console.log('✅ Parsed YouTube links as array:', youtubeLinks);
              }
            } else if (admin.youtube.trim()) {
              // Single string URL
              youtubeLinks = [admin.youtube];
              console.log('✅ Converted YouTube string to array:', youtubeLinks);
            }
          }
        } catch (e) {
          console.log('⚠️ Error parsing YouTube data, using default');
        }
      }
      
      // Initialize images gallery
      const images = getAllImages();
      setAllImages(images.profile);
      setLogoImages(images.logo);
      console.log('✅ Initialized image gallery with', images.profile.length, 'profile images and', images.logo.length, 'logo images');

      // Handle social media data - could be JSON array string or actual array
      let socialData = '';
      let parsedTags = [];
      if (admin.social) {
        try {
          let socialArray = [];
          if (typeof admin.social === 'string' && admin.social.startsWith('[')) {
            // If it's a JSON array, parse it
            socialArray = JSON.parse(admin.social);
          } else if (typeof admin.social === 'string') {
            // If it's a comma-separated string
            socialArray = admin.social.split(', ').filter(url => url.trim());
          } else if (Array.isArray(admin.social)) {
            socialArray = admin.social;
          }

          // Convert URLs to tags
          if (Array.isArray(socialArray)) {
            socialData = socialArray.join(', ');
            parsedTags = socialArray.map(url => {
              // Parse URL to extract platform and username
              const urlLower = url.toLowerCase();
              
              // Check each platform
              if (urlLower.includes('youtube.com')) {
                const username = url.split('@')[1] || url.split('/').pop();
                return {
                  platform: 'YouTube',
                  username: username,
                  icon: '▶️',
                  color: 'bg-red-500',
                  useAt: true
                };
              } else if (urlLower.includes('tiktok.com')) {
                const username = url.split('@')[1] || url.split('/').pop();
                return {
                  platform: 'TikTok',
                  username: username,
                  icon: '🎵',
                  color: 'bg-black',
                  useAt: true
                };
              } else if (urlLower.includes('instagram.com')) {
                const username = url.split('instagram.com/')[1]?.replace('/', '') || url.split('/').pop();
                return {
                  platform: 'Instagram',
                  username: username,
                  icon: '📷',
                  color: 'bg-pink-500',
                  useAt: false
                };
              } else if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) {
                const username = url.split('twitter.com/')[1]?.replace('/', '') || url.split('x.com/')[1]?.replace('/', '') || url.split('/').pop();
                return {
                  platform: 'X',
                  username: username,
                  icon: '𝕏',
                  color: 'bg-blue-400',
                  useAt: false
                };
              } else if (urlLower.includes('facebook.com')) {
                const username = url.split('facebook.com/')[1]?.replace('/', '') || url.split('/').pop();
                return {
                  platform: 'Facebook',
                  username: username,
                  icon: '👤',
                  color: 'bg-blue-600',
                  useAt: false
                };
              } else if (urlLower.includes('twitch.tv')) {
                const username = url.split('twitch.tv/')[1]?.replace('/', '') || url.split('/').pop();
                return {
                  platform: 'Twitch',
                  username: username,
                  icon: '🎮',
                  color: 'bg-purple-500',
                  useAt: false
                };
              } else if (urlLower.includes('discord.com')) {
                const username = url.split('users/')[1]?.replace('/', '') || url.split('/').pop();
                return {
                  platform: 'Discord',
                  username: username,
                  icon: '💬',
                  color: 'bg-indigo-500',
                  useAt: false
                };
              }
              
              return null;
            }).filter(tag => tag !== null);
          }
        } catch (e) {
          console.log('⚠️ Error parsing social data, using as string');
          socialData = admin.social;
        }
      }

      // Set social media tags
      setSocialMediaTags(parsedTags);

      setSettings(prev => ({
        ...prev,
        information: {
          ...prev.information,
          name: admin.name || '',
          country: admin.country || '',
          social: socialData,
          subscriber: admin.subscriber || '',
          url: admin.url || '',
          youtube: youtubeLinks,
          about: admin.about || '',
          message: admin.message || '',
          profile: admin.profile || 'Streamer', // Load profile type from admin
          member_counter: admin.member_counter || '', // Load member counter from admin
          monthly_traffic: admin.monthly_traffic || '' // Load monthly traffic from admin
        }
      }));

      // Initialize services from admin data
      console.log('🔍 Initializing services from admin data...');
      console.log('📦 admin.services raw data:', admin.services);
      
      if (admin.services) {
        try {
          const parsedServices = JSON.parse(admin.services);
          console.log('📦 Parsed services data:', parsedServices);
          
          if (parsedServices && typeof parsedServices === 'object') {
            // Convert from {0: [image, url, title], 1: [...]} format to array
            const servicesArray = Object.keys(parsedServices).map((key, index) => {
              const serviceData = parsedServices[key];
              console.log(`🔧 Processing service ${key}:`, serviceData);
              
              if (Array.isArray(serviceData) && serviceData.length >= 3) {
                const service = {
                  id: Date.now() + index, // Generate unique ID
                  imageUrl: serviceData[0],
                  imagePreview: serviceData[0],
                  url: serviceData[1],
                  title: serviceData[2]
                };
                console.log('✅ Created service object:', service);
                return service;
              }
              console.log('❌ Invalid service data format for key:', key);
              return null;
            }).filter(service => service !== null);
            
            console.log('📋 Final services array:', servicesArray);
            setServices(servicesArray);
          } else {
            console.log('❌ Parsed services is not an object:', typeof parsedServices);
            setServices([]);
          }
        } catch (e) {
          console.error('❌ Error parsing services data:', e);
          setServices([]);
        }
      } else {
        console.log('ℹ️ No services data found in admin');
        setServices([]);
      }
    }
  }, [admin, isAuthenticated, getAllImages, socialPlatforms]);

  // Initialize location data from admin country
  useEffect(() => {
    if (admin?.country && settings.information.country) {
      // Try to find matching country in the list
      const matchingCountry = countries.find(country => 
        country.name.toLowerCase() === settings.information.country.toLowerCase()
      );
      
      if (matchingCountry) {
        const flagClass = getFlagClass(matchingCountry.code);
        setLocationData({
          country: matchingCountry.name,
          countryCode: matchingCountry.code,
          flagClass: flagClass
        });
      } else {
        // If no match found, just set the country name
        setLocationData({
          country: settings.information.country,
          countryCode: null,
          flagClass: null
        });
      }
    }
  }, [admin?.country, settings.information.country]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLocationDropdown && !event.target.closest('.location-dropdown')) {
        setShowLocationDropdown(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLocationDropdown]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSettingChange = (tab, field, value) => {
    // Special handling for message and about fields when they are multilingual
    if ((field === 'message' || field === 'about') && isMultilingualField(settings[tab][field])) {
      // For multilingual fields, we need to update the specific language key
      let existingData = {};
      const originalFieldValue = settings[tab][field];
      
      // Parse existing multilingual data
      if (typeof originalFieldValue === 'string') {
        try {
          existingData = JSON.parse(originalFieldValue);
        } catch (e) {
          existingData = {};
        }
      } else if (typeof originalFieldValue === 'object' && originalFieldValue !== null) {
        existingData = { ...originalFieldValue };
      }
      
      // Update the appropriate language key based on sidebar selection
      if (currentLanguage === 'en') {
        existingData.english = value;
      } else if (currentLanguage === 'fr') {
        existingData.french = value;
      }
      
      // Store the updated multilingual object
      setSettings(prev => ({
        ...prev,
        [tab]: {
          ...prev[tab],
          [field]: existingData
        }
      }));
    } else {
      // Normal handling for non-multilingual fields
      setSettings(prev => ({
        ...prev,
        [tab]: {
          ...prev[tab],
          [field]: value
        }
      }));
    }
  };

  // Handle YouTube link changes
  const handleYoutubeChange = (index, value) => {
    setSettings(prev => ({
      ...prev,
      information: {
        ...prev.information,
        youtube: prev.information.youtube.map((link, i) => 
          i === index ? value : link
        )
      }
    }));
  };

  // Add new YouTube link input
  const addYoutubeLink = () => {
    setSettings(prev => ({
      ...prev,
      information: {
        ...prev.information,
        youtube: [...prev.information.youtube, '']
      }
    }));
  };

  // Remove YouTube link input
  const removeYoutubeLink = (index) => {
    if (settings.information.youtube.length > 1) {
      setSettings(prev => ({
        ...prev,
        information: {
          ...prev.information,
          youtube: prev.information.youtube.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  // Service management functions
  const handleServiceImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Just create preview, don't upload yet
      setNewService(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleAddService = () => {
    if (services.length >= 4) {
      showToastMessage('Maximum 4 services allowed', 'error');
      return;
    }
    setShowServiceModal(true);
  };

  const handleSaveService = async () => {
    // For editing: allow existing image, for new: require new image
    if (!newService.title.trim() || !newService.url.trim() || (!newService.image && !newService.cloudinaryUrl)) {
      showToastMessage('Please fill all fields and select an image', 'error');
      return;
    }

    try {
      // Set uploading state
      setNewService(prev => ({ ...prev, isUploading: true }));

      let cloudinaryUrl = newService.cloudinaryUrl;
      let cloudinaryPublicId = newService.cloudinaryPublicId;

      // Upload to Cloudinary only if new image is selected
      if (newService.image) {
        // Upload to Cloudinary - use same pattern as handleImageUpload
        
        // Get streamer name for folder structure
        const streamerName = admin?.owner || admin?.name || 'unknown-streamer';
        console.log('📁 Creating folder for streamer:', streamerName);
        
        const signatureResponse = await fetch('/.netlify/functions/get-cloudinary-signature', adminFetchInit({
          method: 'POST',
          body: JSON.stringify({
            streamerName: streamerName,
          }),
        }));

        if (!signatureResponse.ok) {
          const errorText = await signatureResponse.text();
          console.error('❌ Signature request failed:', errorText);
          throw new Error(`Failed to get upload signature: ${errorText}`);
        }

        const signatureData = await signatureResponse.json();
        console.log('✅ Got Cloudinary signature:', signatureData);

        console.log('📤 Uploading service image to Cloudinary...');
        console.log(`  - File: ${newService.image.name} (${(newService.image.size / (1024 * 1024)).toFixed(1)}MB)`);

        const cloudinaryFormData = new FormData();
        cloudinaryFormData.append('file', newService.image);
        cloudinaryFormData.append('api_key', signatureData.apiKey);
        cloudinaryFormData.append('timestamp', signatureData.timestamp);
        cloudinaryFormData.append('signature', signatureData.signature);
        cloudinaryFormData.append('folder', signatureData.folder);

        console.log('📋 Cloudinary upload parameters:');
        console.log('  - API Key:', signatureData.apiKey);
        console.log('  - Timestamp:', signatureData.timestamp);
        console.log('  - Folder:', signatureData.folder);
        console.log('  - Cloud Name:', signatureData.cloudName);

        const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`, {
          method: 'POST',
          body: cloudinaryFormData
        });

        if (!cloudinaryResponse.ok) {
          const errorText = await cloudinaryResponse.text();
          throw new Error(`Failed to upload image: ${errorText}`);
        }

        const cloudinaryResult = await cloudinaryResponse.json();
        console.log('✅ Service image uploaded to Cloudinary:', cloudinaryResult.secure_url);
        
        cloudinaryUrl = cloudinaryResult.secure_url;
        cloudinaryPublicId = cloudinaryResult.public_id;
      }

      let updatedServices;
      
      if (editingService) {
        // Edit existing service
        updatedServices = services.map(service => 
          service.id === editingService.id 
            ? {
                ...service,
                title: newService.title.trim(),
                url: newService.url.trim(),
                imageUrl: cloudinaryUrl,
                imagePreview: cloudinaryUrl,
                cloudinaryPublicId: cloudinaryPublicId
              }
            : service
        );
      } else {
        // Create new service
        const serviceToAdd = {
          id: Date.now(),
          title: newService.title.trim(),
          url: newService.url.trim(),
          imageUrl: cloudinaryUrl,
          imagePreview: cloudinaryUrl,
          cloudinaryPublicId: cloudinaryPublicId
        };
        updatedServices = [...services, serviceToAdd];
      }

      // Update local state
      setServices(updatedServices);

      // Immediately save to database
      console.log('💾 Saving service directly to database...');
      
      // Prepare services data for database in the correct format
      let servicesData = {};
      updatedServices.forEach((service, index) => {
        servicesData[index] = [
          service.imageUrl,           // image URL
          service.url,               // service URL  
          service.title              // caption/title
        ];
      });

      // Save to database immediately
      const updateResponse = await fetch('/.netlify/functions/update-admin-info', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          services: JSON.stringify(servicesData),
        }),
      }));

      if (!updateResponse.ok) {
        // If database save fails, remove from local state
        setServices(services);
        throw new Error('Failed to save service to database');
      }

      console.log('✅ Service saved to database successfully!');
      
      // Reset form
      setNewService({
        title: '',
        url: '',
        image: null,
        imagePreview: null,
        cloudinaryUrl: null,
        cloudinaryPublicId: null,
        isUploading: false
      });
      
      setShowServiceModal(false);
      setEditingService(null);
      showToastMessage(editingService ? 'Service updated successfully!' : 'Service added successfully!', 'success');

    } catch (error) {
      console.error('Error uploading image or saving service:', error);
      setNewService(prev => ({ ...prev, isUploading: false }));
      showToastMessage(`Failed to save service: ${error.message}`, 'error');
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      // Find the service to delete to get its image info
      const serviceToDelete = services.find(service => service.id === serviceId);
      if (!serviceToDelete) {
        throw new Error('Service not found');
      }

      // Remove from local state first
      const updatedServices = services.filter(service => service.id !== serviceId);
      setServices(updatedServices);

      // Extract public_id from Cloudinary URL for deletion
      let publicId = null;
      if (serviceToDelete.imageUrl) {
        try {
          const urlParts = serviceToDelete.imageUrl.split('/');
          const uploadIndex = urlParts.indexOf('upload');
          if (uploadIndex !== -1 && uploadIndex < urlParts.length - 1) {
            // Skip version if present (v1234567890)
            let startIndex = uploadIndex + 1;
            if (urlParts[startIndex] && urlParts[startIndex].startsWith('v')) {
              startIndex += 1;
            }
            // Join the remaining parts and remove the file extension
            const pathParts = urlParts.slice(startIndex);
            publicId = pathParts.join('/').replace(/\.[^/.]+$/, '');
          }
        } catch (error) {
          console.warn('Could not extract public_id from service image URL:', error);
        }
      }

      // Delete from Cloudinary if we have a public_id
      if (publicId) {
        try {
          const deleteResponse = await fetch('/.netlify/functions/delete-cloudinary-image', adminFetchInit({
            method: 'POST',
            body: JSON.stringify({
              publicId: publicId,
            }),
          }));

          if (!deleteResponse.ok) {
            console.warn('Failed to delete service image from Cloudinary, but continuing with database update');
          } else {
            console.log('✅ Service image deleted from Cloudinary:', publicId);
          }
        } catch (cloudinaryError) {
          console.warn('Error deleting service image from Cloudinary:', cloudinaryError);
          // Continue with database update even if Cloudinary deletion fails
        }
      }

      // Save updated services to database
      // Prepare services data for database in the correct format
      let servicesData = {};
      updatedServices.forEach((service, index) => {
        servicesData[index] = [
          service.imageUrl,           // image URL
          service.url,               // service URL  
          service.title              // caption/title
        ];
      });

      // Save to database
      const updateResponse = await fetch('/.netlify/functions/update-admin-info', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          services: JSON.stringify(servicesData),
        }),
      }));

      if (!updateResponse.ok) {
        // If database save fails, revert local state
        setServices(services);
        throw new Error('Failed to remove service from database');
      }

      console.log('✅ Service removed from database successfully!');
      showToastMessage('Service removed successfully!', 'success');
    } catch (error) {
      console.error('Error removing service:', error);
      showToastMessage('Error removing service', 'error');
      // Revert local state on error
      const originalServices = services.filter(service => service.id !== serviceId);
      setServices([...originalServices, services.find(service => service.id === serviceId)]);
    }
  };

  const closeServiceModal = () => {
    setShowServiceModal(false);
    setEditingService(null); // Clear editing state
    setNewService({
      title: '',
      url: '',
      image: null,
      imagePreview: null,
      cloudinaryUrl: null,
      cloudinaryPublicId: null,
      isUploading: false
    });
  };

  // Handle editing a service
  const handleEditService = (service) => {
    setEditingService(service);
    setNewService({
      title: service.title,
      url: service.url,
      image: null,
      imagePreview: service.imageUrl, // Show existing image as preview
      cloudinaryUrl: service.imageUrl,
      cloudinaryPublicId: service.cloudinaryPublicId || null,
      isUploading: false
    });
    setShowServiceModal(true);
  };

  // Language modal functions
  const openLanguageModal = (type) => {
    setLanguageModalType(type);
    setLanguageForm({ country: '', text: '' });
    setShowLanguageModal(true);
  };

  const closeLanguageModal = () => {
    setShowLanguageModal(false);
    setLanguageModalType('');
    setLanguageForm({ country: '', text: '' });
    setShowCountryDropdown(false); // Close dropdown when modal closes
  };

  const handleLanguageSubmit = async () => {
    if (!languageForm.country.trim() || !languageForm.text.trim()) {
      showToastMessage('Please select a country and enter text', 'error');
      return;
    }

    try {
      // Get current field value
      const currentFieldValue = settings.information[languageModalType];
      
      // Determine language name based on country code
      const languageName = languageForm.country === 'FR' ? 'french' : 
                          languageForm.country === 'US' ? 'english' : 
                          languageForm.country.toLowerCase();

      let updatedFieldValue;
      const isExistingContent = hasExistingLanguageContent();

      if (isExistingContent) {
        // **UPDATE MODE**: Content exists for this language - just update the specific key
        console.log('UPDATE MODE: Updating existing language content');
        
        // Parse current value if it's a string
        let currentObject = currentFieldValue;
        if (typeof currentFieldValue === 'string') {
          try {
            currentObject = JSON.parse(currentFieldValue);
          } catch (e) {
            console.error('Error parsing current field value:', e);
            throw new Error('Invalid current field format');
          }
        }
        
        // Simply update the specific language key
        updatedFieldValue = {
          ...currentObject,
          [languageName]: languageForm.text.trim()
        };
        
      } else {
        // **INSERT MODE**: No content exists for this language - create or add to structure
        console.log('INSERT MODE: Adding new language content');
        
        if (!currentFieldValue || typeof currentFieldValue !== 'object') {
          // Field is empty or simple text - create new multilingual structure
          if (typeof currentFieldValue === 'string' && currentFieldValue.trim()) {
            // There's existing simple text - convert to multilingual
            const oppositeLanguageKey = languageForm.country === 'FR' ? 'english' : 'french';
            updatedFieldValue = {
              [oppositeLanguageKey]: currentFieldValue.trim(), // Existing text goes to opposite language
              [languageName]: languageForm.text.trim() // New language content
            };
          } else {
            // Field is empty - create new structure with just this language
            updatedFieldValue = {
              [languageName]: languageForm.text.trim()
            };
          }
        } else {
          // Field is already multilingual object - add new language
          let currentObject = currentFieldValue;
          if (typeof currentFieldValue === 'string') {
            try {
              currentObject = JSON.parse(currentFieldValue);
            } catch (e) {
              console.error('Error parsing current field value:', e);
              throw new Error('Invalid current field format');
            }
          }
          
          updatedFieldValue = {
            ...currentObject,
            [languageName]: languageForm.text.trim()
          };
        }
      }

      console.log('Final updated value:', updatedFieldValue);

      // IMPORTANT: Only update the specific field (message OR about, not both)
      // languageModalType contains which field is being edited ('message' or 'about')
      // ✅ EMOJI SUPPORT: All emojis and UTF-8 characters are preserved when saving
      const updateData = {
        [languageModalType]: JSON.stringify(updatedFieldValue)
      };
      
      console.log('=== LANGUAGE UPDATE DEBUG ===');
      console.log('Field being updated:', languageModalType);
      console.log('Update data being sent:', updateData);
      console.log('✅ Emojis preserved in update');
      console.log('=============================');

      const response = await fetch('/.netlify/functions/update-admin-info', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(updateData),
      }));

      if (!response.ok) {
        throw new Error('Failed to save language version');
      }

      // Update local state
      setSettings(prev => ({
        ...prev,
        information: {
          ...prev.information,
          [languageModalType]: updatedFieldValue
        }
      }));

      const actionText = isExistingContent ? 'updated' : 'added';
      showToastMessage(`${languageModalType.charAt(0).toUpperCase() + languageModalType.slice(1)} language ${actionText} successfully!`, 'success');
      closeLanguageModal();
    } catch (error) {
      console.error('Error saving language version:', error);
      showToastMessage('Failed to save language version. Please try again.', 'error');
    }
  };

  // Custom dropdown state for language modal
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const selectCountry = (countryCode) => {
    // Update country selection
    setLanguageForm(prev => ({ ...prev, country: countryCode }));
    setShowCountryDropdown(false);
    
    // Smart content detection and pre-population
    const currentFieldValue = settings.information[languageModalType];
    
    // Determine language name based on country code
    const languageName = countryCode === 'FR' ? 'french' : 
                        countryCode === 'US' ? 'english' : 
                        countryCode.toLowerCase();
    
    let existingContent = '';
    
    // Check if current field is multilingual (object/array)
    if (currentFieldValue && typeof currentFieldValue === 'object') {
      // Extract existing content for this language
      existingContent = currentFieldValue[languageName] || '';
    } else if (typeof currentFieldValue === 'string' && currentFieldValue.trim()) {
      try {
        // Try to parse as JSON in case it's a stringified object
        const parsed = JSON.parse(currentFieldValue);
        if (typeof parsed === 'object' && parsed !== null) {
          existingContent = parsed[languageName] || '';
        }
      } catch (e) {
        // Not JSON, so it's simple text - leave empty for new language
        existingContent = '';
      }
    }
    
    // Pre-populate textarea with existing content
    setLanguageForm(prev => ({ 
      ...prev, 
      country: countryCode,
      text: existingContent 
    }));
  };

  // Helper function to check if language content exists
  const hasExistingLanguageContent = () => {
    if (!languageForm.country || !languageModalType) return false;
    
    const currentFieldValue = settings.information[languageModalType];
    const languageName = languageForm.country === 'FR' ? 'french' : 
                        languageForm.country === 'US' ? 'english' : 
                        languageForm.country.toLowerCase();
    
    // Check if current field is multilingual and has content for this language
    if (currentFieldValue && typeof currentFieldValue === 'object') {
      return !!currentFieldValue[languageName];
    } else if (typeof currentFieldValue === 'string' && currentFieldValue.trim()) {
      try {
        const parsed = JSON.parse(currentFieldValue);
        if (typeof parsed === 'object' && parsed !== null) {
          return !!parsed[languageName];
        }
      } catch (e) {
        // Not JSON, so it's simple text
        return false;
      }
    }
    
    return false;
  };

  // Handle multiple image upload (up to 5)
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const maxFiles = 5;
    const currentTotal = allImages.length + newImages.length;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

    // Check total limit
    if (currentTotal >= maxFiles) {
      setImageUploadStatus('error');
      setImageUploadMessage(
        t('language') === 'fr'
          ? `Limite de ${maxFiles} images atteinte`
          : `${maxFiles} image limit reached`
      );
      e.target.value = '';
      return;
    }

    // Check how many files we can add
    const availableSlots = maxFiles - currentTotal;
    const filesToProcess = files.slice(0, availableSlots);
    
    if (files.length > availableSlots) {
      setImageUploadMessage(
        t('language') === 'fr'
          ? `Seulement ${availableSlots} image(s) ajoutée(s) (limite de ${maxFiles})`
          : `Only ${availableSlots} image(s) added (${maxFiles} max)`
      );
    }

    const validFiles = [];
    const errorMessages = [];

    // Validate each file
    filesToProcess.forEach((file, index) => {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        errorMessages.push(
          t('language') === 'fr'
            ? `${file.name}: Format non valide`
            : `${file.name}: Invalid format`
        );
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        errorMessages.push(
          t('language') === 'fr'
            ? `${file.name}: Trop volumineux (max 10MB)`
            : `${file.name}: Too large (max 10MB)`
        );
        return;
      }

      validFiles.push(file);
    });

    // Show errors if any
    if (errorMessages.length > 0) {
      setImageUploadStatus('error');
      setImageUploadMessage(errorMessages.join(', '));
    }

    // Process valid files
    if (validFiles.length > 0) {
      const newPreviews = [...newImagePreviews];
      const newPhotos = [...newImages];
      let loadedCount = 0;

      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          newPhotos.push(file);
          loadedCount++;
          
          // Update state when all files are loaded
          if (loadedCount === validFiles.length) {
            setNewImagePreviews(newPreviews);
            setNewImages(newPhotos);
            setImageUploadStatus('success');
            setImageUploadMessage(
              t('language') === 'fr'
                ? `${validFiles.length} image(s) sélectionnée(s) (${newPhotos.length} prêtes à télécharger)`
                : `${validFiles.length} image(s) selected (${newPhotos.length} ready to upload)`
            );
          }
        };
        reader.readAsDataURL(file);
      });
    }
    
    // Clear the input for next selection
    e.target.value = '';
  };

  // Function to remove a specific new image before upload
  const handleRemoveNewImage = (indexToRemove) => {
    const updatedImages = newImages.filter((_, index) => index !== indexToRemove);
    const updatedPreviews = newImagePreviews.filter((_, index) => index !== indexToRemove);
    
    setNewImages(updatedImages);
    setNewImagePreviews(updatedPreviews);
    
    if (updatedImages.length > 0) {
      setImageUploadMessage(
        t('language') === 'fr'
          ? `${updatedImages.length} image(s) prêtes à télécharger`
          : `${updatedImages.length} image(s) ready to upload`
      );
    } else {
      setImageUploadStatus(null);
      setImageUploadMessage('');
    }
  };

  // Update profile image using direct Cloudinary upload
  const handleUpdateImage = async () => {
    if (newImages.length === 0) {
      showToastMessage(
        t('language') === 'fr'
          ? 'Veuillez d\'abord sélectionner des images'
          : 'Please select images first',
        'error'
      );
      return;
    }

    setIsUpdatingImage(true);
    setImageUploadStatus('uploading');
    setUploadProgress(0);
    setImageUploadMessage(
      t('language') === 'fr'
        ? 'Préparation du téléchargement...'
        : 'Preparing upload...'
    );

    try {
      // Step 1: Get Cloudinary signature
      console.log('🔐 Getting Cloudinary signature...');
      
      // Get streamer name for folder structure
      const streamerName = admin?.owner || admin?.name || 'unknown-streamer';
      console.log('📁 Creating folder for streamer:', streamerName);
      
      const signatureResponse = await fetch('/.netlify/functions/get-cloudinary-signature', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          streamerName: streamerName,
        }),
      }));

      if (!signatureResponse.ok) {
        throw new Error('Failed to get Cloudinary signature');
      }

      const signatureData = await signatureResponse.json();
      console.log('✅ Got Cloudinary signature');

      // Step 2: Upload all images to Cloudinary
      const imageUrls = [];
      const totalImages = newImages.length;

      for (let i = 0; i < newImages.length; i++) {
        const image = newImages[i];
        
        setImageUploadMessage(
          t('language') === 'fr'
            ? `Téléchargement ${i + 1}/${totalImages}...`
            : `Uploading ${i + 1}/${totalImages}...`
        );
        setUploadProgress(((i + 1) / totalImages) * 100);

        console.log(`📤 Uploading image ${i + 1}/${totalImages} to Cloudinary...`);
        console.log(`  - File: ${image.name} (${(image.size / (1024 * 1024)).toFixed(1)}MB)`);

        const cloudinaryFormData = new FormData();
        cloudinaryFormData.append('file', image);
        cloudinaryFormData.append('api_key', signatureData.apiKey);
        cloudinaryFormData.append('timestamp', signatureData.timestamp);
        cloudinaryFormData.append('signature', signatureData.signature);
        cloudinaryFormData.append('folder', signatureData.folder);

        const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`, {
          method: 'POST',
          body: cloudinaryFormData
        });

        if (!cloudinaryResponse.ok) {
          const errorText = await cloudinaryResponse.text();
          throw new Error(`Failed to upload image ${i + 1}: ${errorText}`);
        }

        const cloudinaryResult = await cloudinaryResponse.json();
        imageUrls.push(cloudinaryResult.secure_url);
        
        console.log(`✅ Image ${i + 1} uploaded to Cloudinary:`, cloudinaryResult.secure_url);
      }

      console.log(`🎉 All ${imageUrls.length} images uploaded successfully!`);

      // Step 3: Add new images to beginning of existing images array
      const updatedProfileImages = [...imageUrls, ...allImages];
      
      // Step 4: Update admin data with new structure {profile: [...], logo: [...]}
      const updatedImageData = {
        profile: updatedProfileImages,
        logo: logoImages // Maintain existing logo
      };
      
      setImageUploadMessage(
        t('language') === 'fr'
          ? 'Mise à jour de la base de données...'
          : 'Updating database...'
      );

      const updateResponse = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          action: 'update',
          image: JSON.stringify(updatedImageData),
        }),
      }));

      if (!updateResponse.ok) {
        throw new Error('Failed to update database');
      }

      const updateResult = await updateResponse.json();

      if (updateResponse.ok && updateResult.success) {
        // Update local state with new images array
        setAllImages(updatedProfileImages);
        setCurrentProfileIndex(0); // First new image becomes profile
        
        // Update admin state with new data from server
        if (updateResult.admin) {
          setAdmin(updateResult.admin);
        }
        
        setImageUploadStatus('success');
        setImageUploadMessage(
          t('language') === 'fr'
            ? `${imageUrls.length} image(s) ajoutée(s) avec succès ✓`
            : `${imageUrls.length} image(s) added successfully ✓`
        );
        
        showToastMessage(
          t('language') === 'fr'
            ? `${imageUrls.length} image(s) ajoutée(s) avec succès!`
            : `${imageUrls.length} image(s) added successfully!`,
          'success'
        );

        // Clear the new image inputs
        setNewImages([]);
        setNewImagePreviews([]);
      } else {
        throw new Error(updateResult.message || 'Failed to add images to database');
      }

    } catch (error) {
      console.error('Error updating images:', error);
      setImageUploadStatus('error');
      setImageUploadMessage(
        t('language') === 'fr'
          ? 'Erreur lors de l\'ajout des images'
          : 'Error adding images'
      );
      showToastMessage(
        t('language') === 'fr'
          ? `Erreur: ${error.message}`
          : `Error: ${error.message}`,
        'error'
      );
    } finally {
      setIsUpdatingImage(false);
      setUploadProgress(0);
    }
  };

  // Handle multiple logo image upload
  const handleLogoImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const maxFiles = 5;
    const currentTotal = logoImages.length + newLogoImages.length;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    // Check total limit
    if (currentTotal >= maxFiles) {
      setImageUploadStatus('error');
      setImageUploadMessage(
        t('language') === 'fr'
          ? `Limite de ${maxFiles} logos atteinte`
          : `${maxFiles} logo limit reached`
      );
      e.target.value = '';
      return;
    }

    // Check how many files we can add
    const availableSlots = maxFiles - currentTotal;
    const filesToProcess = files.slice(0, availableSlots);
    
    if (files.length > availableSlots) {
      setImageUploadMessage(
        t('language') === 'fr'
          ? `Seulement ${availableSlots} logo(s) ajouté(s) (limite de ${maxFiles})`
          : `Only ${availableSlots} logo(s) added (${maxFiles} max)`
      );
    }

    const validFiles = [];
    const previews = [];

    filesToProcess.forEach((file) => {
      // Check file type
      if (!allowedTypes.includes(file.type)) {
        setImageUploadStatus('error');
        setImageUploadMessage(
          t('language') === 'fr'
            ? `${file.name}: Type de fichier non supporté`
            : `${file.name}: Unsupported file type`
        );
        return;
      }

      // Check file size
      if (file.size > maxSize) {
        setImageUploadStatus('error');
        setImageUploadMessage(
          t('language') === 'fr'
            ? `${file.name}: Fichier trop volumineux (max 10MB)`
            : `${file.name}: File too large (max 10MB)`
        );
        return;
      }

      validFiles.push(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push(reader.result);
        if (previews.length === validFiles.length) {
          setNewLogoImagePreviews([...newLogoImagePreviews, ...previews]);
          setImageUploadStatus(null);
        }
      };
      reader.readAsDataURL(file);
    });

    if (validFiles.length > 0) {
      setNewLogoImages([...newLogoImages, ...validFiles]);
      setImageUploadMessage(
        t('language') === 'fr'
          ? `${validFiles.length} logo(s) prêt(s) à télécharger`
          : `${validFiles.length} logo(s) ready to upload`
      );
    }

    e.target.value = '';
  };

  // Remove a specific new logo image before upload
  const handleRemoveNewLogoImage = (indexToRemove) => {
    const updatedImages = newLogoImages.filter((_, index) => index !== indexToRemove);
    const updatedPreviews = newLogoImagePreviews.filter((_, index) => index !== indexToRemove);
    
    setNewLogoImages(updatedImages);
    setNewLogoImagePreviews(updatedPreviews);
    
    if (updatedImages.length > 0) {
      setImageUploadMessage(
        t('language') === 'fr'
          ? `${updatedImages.length} logo(s) prêt(s) à télécharger`
          : `${updatedImages.length} logo(s) ready to upload`
      );
    } else {
      setImageUploadStatus(null);
      setImageUploadMessage('');
    }
  };

  // Update logo images
  const handleUpdateLogoImage = async () => {
    if (newLogoImages.length === 0) {
      showToastMessage(
        t('language') === 'fr'
          ? 'Veuillez d\'abord sélectionner des images'
          : 'Please select images first',
        'error'
      );
      return;
    }

    setIsUpdatingLogo(true);
    setImageUploadStatus('uploading');
    setUploadProgress(0);
    setImageUploadMessage(
      t('language') === 'fr'
        ? 'Préparation du téléchargement...'
        : 'Preparing upload...'
    );

    try {
      // Step 1: Get Cloudinary signature
      console.log('🔐 Getting Cloudinary signature for logos...');
      
      const streamerName = admin?.owner || admin?.name || 'unknown-streamer';
      console.log('📁 Creating folder for streamer:', streamerName);
      
      const signatureResponse = await fetch('/.netlify/functions/get-cloudinary-signature', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          streamerName: streamerName,
        }),
      }));

      if (!signatureResponse.ok) {
        throw new Error('Failed to get Cloudinary signature');
      }

      const signatureData = await signatureResponse.json();
      console.log('✅ Got Cloudinary signature');

      // Step 2: Upload all logos to Cloudinary
      const logoUrls = [];
      const totalLogos = newLogoImages.length;

      for (let i = 0; i < newLogoImages.length; i++) {
        const logo = newLogoImages[i];
        
        setImageUploadMessage(
          t('language') === 'fr'
            ? `Téléchargement ${i + 1}/${totalLogos}...`
            : `Uploading ${i + 1}/${totalLogos}...`
        );
        setUploadProgress(((i + 1) / totalLogos) * 100);

        console.log(`📤 Uploading logo ${i + 1}/${totalLogos} to Cloudinary...`);
        console.log(`  - File: ${logo.name} (${(logo.size / (1024 * 1024)).toFixed(1)}MB)`);

        const cloudinaryFormData = new FormData();
        cloudinaryFormData.append('file', logo);
        cloudinaryFormData.append('api_key', signatureData.apiKey);
        cloudinaryFormData.append('timestamp', signatureData.timestamp);
        cloudinaryFormData.append('signature', signatureData.signature);
        cloudinaryFormData.append('folder', signatureData.folder);

        const cloudinaryResponse = await fetch(`https://api.cloudinary.com/v1_1/${signatureData.cloudName}/image/upload`, {
          method: 'POST',
          body: cloudinaryFormData
        });

        if (!cloudinaryResponse.ok) {
          const errorText = await cloudinaryResponse.text();
          throw new Error(`Failed to upload logo ${i + 1}: ${errorText}`);
        }

        const cloudinaryResult = await cloudinaryResponse.json();
        logoUrls.push(cloudinaryResult.secure_url);
        
        console.log(`✅ Logo ${i + 1} uploaded to Cloudinary:`, cloudinaryResult.secure_url);
      }

      console.log(`🎉 All ${logoUrls.length} logos uploaded successfully!`);

      // Step 3: Update admin data with new structure {profile: [...], logo: [...]}
      setImageUploadMessage(
        t('language') === 'fr'
          ? 'Mise à jour de la base de données...'
          : 'Updating database...'
      );

      // Add new logos to beginning of existing logos array
      const updatedLogos = [...logoUrls, ...logoImages];

      const updatedImageData = {
        profile: allImages,
        logo: updatedLogos
      };

      const updateResponse = await fetch('/.netlify/functions/admin-auth', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          action: 'update',
          image: JSON.stringify(updatedImageData),
        }),
      }));

      if (!updateResponse.ok) {
        throw new Error('Failed to update database');
      }

      const updateResult = await updateResponse.json();
      setUploadProgress(100);

      if (updateResponse.ok && updateResult.success) {
        // Update local state with new logos array
        setLogoImages(updatedLogos);
        
        // Update admin state with new data from server
        if (updateResult.admin) {
          setAdmin(updateResult.admin);
        }
        
        setImageUploadStatus('success');
        setImageUploadMessage(
          t('language') === 'fr'
            ? `${logoUrls.length} logo(s) ajouté(s) avec succès ✓`
            : `${logoUrls.length} logo(s) added successfully ✓`
        );
        
        showToastMessage(
          t('language') === 'fr'
            ? `${logoUrls.length} logo(s) ajouté(s) avec succès!`
            : `${logoUrls.length} logo(s) added successfully!`,
          'success'
        );

        // Clear the new logo inputs
        setNewLogoImages([]);
        setNewLogoImagePreviews([]);
      } else {
        throw new Error(updateResult.message || 'Failed to add logo to database');
      }

    } catch (error) {
      console.error('Error updating logo:', error);
      setImageUploadStatus('error');
      setImageUploadMessage(
        t('language') === 'fr'
          ? 'Erreur lors de l\'ajout du logo'
          : 'Error adding logo'
      );
      showToastMessage(
        t('language') === 'fr'
          ? `Erreur: ${error.message}`
          : `Error: ${error.message}`,
        'error'
      );
    } finally {
      setIsUpdatingLogo(false);
      setUploadProgress(0);
    }
  };

  const handleUpdateInformation = async () => {
    setIsUpdatingInfo(true);
    try {
      // Filter out empty YouTube links and prepare JSON array
      const validYoutubeLinks = settings.information.youtube.filter(link => link.trim() !== '');
      
      // Convert social media string to array (already formatted URLs from tags)
      const socialArray = settings.information.social 
        ? settings.information.social.split(', ').filter(url => url.trim())
        : [];
      
      // Clean HTML content by removing leading <br> tags, empty elements, and extra <br> tags
      // Clean HTML content by removing leading <br> tags, empty elements, and extra <br> tags
      // ✅ EMOJI SUPPORT: This function preserves all emojis and UTF-8 characters
      const cleanHtmlContent = (html) => {
        if (!html || typeof html !== 'string') return '';
        
        let cleaned = html;
        
        // Remove leading <br> tags, empty paragraphs, divs, spans
        // ⚠️ Note: These regex patterns ONLY target HTML tags, NOT emojis or UTF-8 characters
        cleaned = cleaned
          .replace(/^(\s*<br\s*\/?>\s*)+/gi, '') // Remove leading br tags
          .replace(/^(\s*<p[^>]*>\s*<\/p>\s*)+/gi, '') // Remove empty paragraphs at start
          .replace(/^(\s*<div[^>]*>\s*<\/div>\s*)+/gi, '') // Remove empty divs at start
          .replace(/^(\s*<span[^>]*>\s*<\/span>\s*)+/gi, '') // Remove empty spans at start
          .replace(/^(\s*&nbsp;\s*)+/gi, '') // Remove leading non-breaking spaces
          .replace(/(\s*<br\s*\/?>\s*){2,}/gi, '<br>') // Replace 2+ consecutive br tags with just 1
          .replace(/(\s*<br\s*\/?>\s*)+$/gi, '') // Remove trailing br tags
          .trim(); // Remove any remaining leading/trailing whitespace
        
        // ✅ Emojis and all UTF-8 characters are preserved and saved to Supabase as-is
        return cleaned;
      };

      // Helper function to prepare multilingual field for update
      const prepareMultilingualField = (fieldValue) => {
        console.log('🔍 prepareMultilingualField input:', fieldValue, 'type:', typeof fieldValue);
        
        // Handle empty/null values
        if (!fieldValue) {
          return '';
        }
        
        // If it's already a JSON string, parse it first
        let workingValue = fieldValue;
        if (typeof fieldValue === 'string') {
          try {
            const parsed = JSON.parse(fieldValue);
            // If it parses to an object, use the parsed version
            if (typeof parsed === 'object' && parsed !== null) {
              workingValue = parsed;
            } else {
              // If it's a simple string (not JSON), clean and return it
              return cleanHtmlContent(fieldValue);
            }
          } catch (e) {
            // Not valid JSON, treat as simple string
            return cleanHtmlContent(fieldValue);
          }
        }
        
        // Now check if it's a multilingual object
        if (typeof workingValue === 'object' && workingValue !== null && !Array.isArray(workingValue)) {
          // It's a multilingual object - clean each language value
          const cleanedData = {};
          Object.keys(workingValue).forEach(key => {
            const langValue = workingValue[key];
            if (langValue) {
              cleanedData[key] = cleanHtmlContent(langValue);
            }
          });
          
          console.log('✅ Cleaned multilingual data:', cleanedData);
          return JSON.stringify(cleanedData);
        }
        
        // Fallback: treat as simple string
        return cleanHtmlContent(String(workingValue));
      };

      // Prepare services data for database - format as {0: [image, url, title], 1: [...], ...}
      console.log('🔧 Current services state:', services);
      let servicesData = null;
      if (services.length > 0) {
        servicesData = {};
        services.forEach((service, index) => {
          servicesData[index] = [
            service.imageUrl,           // image URL
            service.url,               // service URL  
            service.title              // caption/title
          ];
        });
      }
      console.log('📦 Prepared services data for database:', servicesData);
      
      const requestData = {
        name: settings.information.name,
        country: settings.information.country,
        social: socialArray.length > 0 ? JSON.stringify(socialArray) : null,
        subscriber: settings.information.subscriber != null && String(settings.information.subscriber).trim() !== '' 
          ? parseInt(String(settings.information.subscriber), 10)
          : null, // Convert to integer or null
        url: settings.information.url || '', // Add URL field
        profile: settings.information.profile, // Add profile type
        member_counter: settings.information.profile === 'Discord' && settings.information.member_counter && settings.information.member_counter.trim() !== '' 
          ? parseInt(settings.information.member_counter, 10) 
          : null, // Only for Discord, convert to number or null
        monthly_traffic: settings.information.profile === 'Website' && settings.information.monthly_traffic && settings.information.monthly_traffic.trim() !== '' 
          ? parseInt(settings.information.monthly_traffic, 10) 
          : null, // Only for Website, convert to number or null
        youtube: validYoutubeLinks.length > 0 ? JSON.stringify(validYoutubeLinks) : null,
        about: prepareMultilingualField(settings.information.about),
        message: prepareMultilingualField(settings.information.message),
        services: servicesData ? JSON.stringify(servicesData) : null
      };
      console.log('📤 Sending data to API:', requestData);
      
      const response = await fetch('/.netlify/functions/update-admin-info', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(requestData),
      }));

      const result = await response.json();

      if (response.ok && result.success) {
        showToastMessage(t('informationUpdatedSuccess'), 'success');
        
        // ✅ Update admin context with fresh data from backend response
        if (result.admin) {
          setAdmin(result.admin);
        } else {
          // Fallback: manually update admin context with local state
          setAdmin(prev => ({
            ...prev,
            name: settings.information.name,
            country: settings.information.country,
            social: socialArray.length > 0 ? JSON.stringify(socialArray) : null,
            subscriber: settings.information.subscriber != null && String(settings.information.subscriber).trim() !== '' 
              ? parseInt(String(settings.information.subscriber), 10) 
              : null,
            url: settings.information.url,
            profile: settings.information.profile,
            member_counter: settings.information.profile === 'Discord' && settings.information.member_counter && settings.information.member_counter.trim() !== '' 
              ? parseInt(settings.information.member_counter, 10) 
              : null,
            monthly_traffic: settings.information.profile === 'Website' && settings.information.monthly_traffic && settings.information.monthly_traffic.trim() !== '' 
              ? parseInt(settings.information.monthly_traffic, 10) 
              : null,
            youtube: validYoutubeLinks.length > 0 ? JSON.stringify(validYoutubeLinks) : null,
            about: prepareMultilingualField(settings.information.about),
            message: prepareMultilingualField(settings.information.message),
            services: servicesData ? JSON.stringify(servicesData) : null
          }));
        }

        // Update local settings state to match what was saved to database
        setSettings(prev => ({
          ...prev,
          information: {
            ...prev.information,
            about: prepareMultilingualField(settings.information.about),
            message: prepareMultilingualField(settings.information.message)
          }
        }));
      } else {
        showToastMessage(result.message || t('failedUpdateInformation'), 'error');
      }
    } catch (error) {
      console.error('Error updating information:', error);
      showToastMessage(t('errorUpdatingInformation'), 'error');
    } finally {
      setIsUpdatingInfo(false);
    }
  };

  // ✅ Separate function for updating profile type only
  const handleUpdateProfile = async () => {
    setIsUpdatingProfile(true);
    try {
      const requestData = {
        profile: settings.information.profile
      };
      
      const response = await fetch('/.netlify/functions/update-admin-info', adminFetchInit({
        method: 'POST',
        body: JSON.stringify(requestData),
      }));

      const result = await response.json();

      if (response.ok && result.success) {
        showToastMessage(
          t('language') === 'fr' 
            ? 'Type de profil mis à jour avec succès!' 
            : 'Profile type updated successfully!', 
          'success'
        );
        
        // ✅ Update admin context with fresh data from backend response
        if (result.admin) {
          setAdmin(result.admin);
        } else {
          // Fallback: manually update admin context
          setAdmin(prev => ({
            ...prev,
            profile: settings.information.profile
          }));
        }
      } else {
        showToastMessage(
          result.message || (t('language') === 'fr' 
            ? 'Échec de la mise à jour du profil' 
            : 'Failed to update profile'), 
          'error'
        );
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showToastMessage(
        t('language') === 'fr' 
          ? 'Erreur lors de la mise à jour du profil' 
          : 'Error updating profile', 
        'error'
      );
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const validatePassword = () => {
    const { currentPassword, newPassword, confirmPassword } = settings.password;
    
    if (!currentPassword) {
      showToastMessage(t('enterCurrentPassword'), 'error');
      return false;
    }
    
    if (!newPassword) {
      showToastMessage(t('enterNewPassword'), 'error');
      return false;
    }
    
    if (newPassword.length < 8) {
      showToastMessage(t('passwordTooShort'), 'error');
      return false;
    }
    
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/.test(newPassword)) {
      showToastMessage(t('passwordRequirementsNotMet'), 'error');
      return false;
    }
    
    if (newPassword !== confirmPassword) {
      showToastMessage(t('passwordsDoNotMatchError'), 'error');
      return false;
    }
    
    return true;
  };

  const handleUpdatePassword = async () => {
    if (!validatePassword()) {
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const response = await fetch('/.netlify/functions/update-admin-password', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({
          currentPassword: settings.password.currentPassword,
          newPassword: settings.password.newPassword,
        }),
      }));

      const result = await response.json();

      if (response.ok && result.success) {
        showToastMessage(t('passwordUpdatedSuccess'), 'success');
        // Clear password fields after successful update
        setSettings(prev => ({
          ...prev,
          password: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }
        }));
      } else {
        showToastMessage(result.message || t('failedUpdatePassword'), 'error');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      showToastMessage(t('errorUpdatingPassword'), 'error');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const renderInformationSettings = () => (
    <div className="p-8">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-2">{t('updateInformationTitle')}</h3>
        <p className="text-sm text-gray-400">{t('manageProfileSocialMedia')}</p>
      </div>
      
      <div className="space-y-6">
        {/* Profile Images Gallery Section */}
        <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-8 border border-gray-600 shadow-xl">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* View Mode Dropdown Button */}
                <div className="relative mt-[-30px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMediaDropdown(!openMediaDropdown);
                    }}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 h-[50px] rounded-lg flex items-center justify-center gap-2 transition-colors shadow-md"
                  >
                    <span className="text-sm font-medium whitespace-nowrap">
                      {t('changePhoto')}
                    </span>
                    <i className="fas fa-chevron-down text-base"></i>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {openMediaDropdown && (
                    <div className="absolute left-0 top-full mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 min-w-[140px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageViewMode('profile');
                          setOpenMediaDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                          imageViewMode === 'profile' ? 'bg-blue-500/20 text-blue-400' : 'text-white hover:bg-gray-700'
                        }`}
                      >
                        <i className="fas fa-user text-blue-400"></i>
                        {t('profilePhoto')}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setImageViewMode('logo');
                          setOpenMediaDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                          imageViewMode === 'logo' ? 'bg-green-500/20 text-green-400' : 'text-white hover:bg-gray-700'
                        }`}
                      >
                        <i className="fas fa-paint-brush text-green-400"></i>
                        {t('logoPhoto')}
                      </button>
                    </div>
                  )}
                </div>
                
                <div>
                  <h4 className="text-2xl font-bold text-white mb-2 flex items-center">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg mr-3">
                      <i className="fas fa-images text-white"></i>
                    </div>
                    {t('mediaGallery')}
                  </h4>
                  <p className="text-gray-400 ml-14">
                    {t('manageImageCollection')}
                    }
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-white">
                  {imageViewMode === 'profile' ? allImages.length : logoImages.length}<span className="text-lg text-gray-400">/{imageViewMode === 'profile' ? '5' : '5'}</span>
                </div>
                <p className="text-xs text-gray-400">
                  {imageViewMode === 'profile' 
                    ? (t('language') === 'fr' ? 'Images' : 'Images')
                    : (t('language') === 'fr' ? 'Logos' : 'Logos')
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Upload New Image Section */}
          {imageViewMode === 'profile' && (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-600 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-lg font-semibold text-white flex items-center">
                  <i className="fas fa-cloud-upload-alt text-purple-500 mr-2"></i>
                  {t('uploadNewImage')}
                </h5>
              </div>

              {/* File Upload Area */}
              <div className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                allImages.length + newImages.length >= 5 
                  ? 'border-gray-500 bg-gray-700/20' 
                  : 'border-purple-500 bg-purple-500/5 hover:bg-purple-500/10 hover:border-purple-400'
              }`}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleImageUpload}
                  disabled={allImages.length + newImages.length >= 5}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  multiple
                />
                <div className={`flex flex-col items-center ${allImages.length + newImages.length >= 5 ? 'text-gray-500' : 'text-purple-400'}`}>
                  <i className="fas fa-images text-4xl mb-3"></i>
                  <h6 className="font-semibold text-white mb-1">
                    {allImages.length + newImages.length >= 5 
                      ? t('language') === 'fr' ? 'Limite atteinte' : 'Limit Reached'
                      : t('dropImagesHere')
                    }
                  </h6>
                  <p className="text-sm text-gray-400 mb-3">
                    {allImages.length + newImages.length >= 5 
                      ? t('language') === 'fr' 
                        ? 'Vous avez atteint la limite de 5 images'
                        : 'You have reached the 5 image limit'
                      : t('language') === 'fr' 
                        ? 'ou cliquez pour sélectionner (multiple)'
                        : 'or click to select (multiple)'
                    }
                  </p>
                  {allImages.length + newImages.length < 5 && (
                    <div className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors">
                      {t('browseFiles')}
                    </div>
                  )}
                </div>
              </div>

            {/* File Info */}
            <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
              <span>
                {t('language') === 'fr' 
                  ? 'Formats supportés: PNG, JPEG, JPG, WebP'
                  : 'Supported formats: PNG, JPEG, JPG, WebP'
                }
              </span>
              <span>
                {t('language') === 'fr' ? 'Max 5 images, 5MB chacune' : 'Max 5 images, 5MB each'}
              </span>
            </div>

            {/* Preview for new images */}
            {newImagePreviews.length > 0 && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <i className="fas fa-clock text-orange-400"></i>
                  <h6 className="text-sm text-orange-400 font-semibold">
                    {t('language') === 'fr' ? 'Prêt à télécharger' : 'Ready to Upload'}
                  </h6>
                  <span className="text-xs text-gray-400">
                    ({newImagePreviews.length} {t('language') === 'fr' ? 'image(s)' : 'image(s)'})
                  </span>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                  {newImagePreviews.map((preview, index) => (
                    <div key={index} className="relative group bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-2">
                      <div className="aspect-square bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Remove button */}
                      <button
                        onClick={() => handleRemoveNewImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center transition-colors shadow-lg"
                        title={t('language') === 'fr' ? 'Supprimer' : 'Remove'}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                      
                      {/* Image info */}
                      <div className="mt-2 text-center">
                        <p className="text-xs text-gray-400 truncate">
                          {newImages[index]?.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {((newImages[index]?.size || 0) / (1024 * 1024)).toFixed(1)}MB
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-gray-400 text-center mt-3">
                  {t('language') === 'fr' 
                    ? 'Ces images seront ajoutées au début de votre galerie'
                    : 'These images will be added to the beginning of your gallery'
                  }
                </p>
              </div>
            )}

            {/* Upload Status */}
            {imageUploadStatus && (
              <div className={`mt-4 p-4 rounded-xl flex items-center ${
                imageUploadStatus === 'success' 
                  ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30' :
                imageUploadStatus === 'error' 
                  ? 'bg-gradient-to-r from-red-500/10 to-rose-500/10 border border-red-500/30' :
                  'bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30'
              }`}>
                <div className="flex items-center flex-1">
                  {imageUploadStatus === 'uploading' && (
                    <i className="fas fa-spinner fa-spin text-blue-400 mr-3"></i>
                  )}
                  {imageUploadStatus === 'success' && (
                    <i className="fas fa-check-circle text-green-400 mr-3"></i>
                  )}
                  {imageUploadStatus === 'error' && (
                    <i className="fas fa-exclamation-triangle text-red-400 mr-3"></i>
                  )}
                  <div className="flex-1">
                    <span className={`text-sm font-medium ${
                      imageUploadStatus === 'success' ? 'text-green-400' :
                      imageUploadStatus === 'error' ? 'text-red-400' : 'text-blue-400'
                    }`}>
                      {imageUploadMessage}
                    </span>
                    
                    {/* Progress bar for uploads */}
                    {imageUploadStatus === 'uploading' && uploadProgress > 0 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-400 mt-1 text-right">
                          {Math.round(uploadProgress)}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Add Images Button */}
            {newImages.length > 0 && (
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleUpdateImage}
                  disabled={isUpdatingImage || allImages.length + newImages.length > 5}
                  className={`px-6 py-3 font-semibold rounded-xl transition-all duration-300 flex items-center shadow-lg ${
                    isUpdatingImage || allImages.length + newImages.length > 5
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white transform hover:scale-105'
                  }`}
                >
                  {isUpdatingImage ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      {t('language') === 'fr' ? 'Téléchargement...' : 'Uploading...'}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-cloud-upload-alt mr-2"></i>
                      {t('language') === 'fr' 
                        ? `Télécharger ${newImages.length} image(s)` 
                        : `Upload ${newImages.length} Image(s)`
                      }
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          )}

          {/* Logo Upload Section */}
          {imageViewMode === 'logo' && (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-600 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-lg font-semibold text-white flex items-center">
                  <i className="fas fa-paint-brush text-green-500 mr-2"></i>
                  {t('uploadLogo')}
                </h5>
              </div>

              <div className="text-sm text-gray-400 mb-4">
                {t('language') === 'fr' 
                  ? 'Téléchargez des images pour votre logo. Le premier logo sera utilisé comme logo principal.'
                  : t('uploadLogoDescription')
                }
              </div>

              {/* File Upload Area */}
              <div className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                logoImages.length >= 5
                  ? 'border-gray-500 bg-gray-700/20' 
                  : 'border-green-500 bg-green-500/5 hover:bg-green-500/10 hover:border-green-400'
              }`}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleLogoImageUpload}
                  disabled={logoImages.length >= 5}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  multiple
                />
                <div className={`flex flex-col items-center ${logoImages.length >= 5 ? 'text-gray-500' : 'text-green-400'}`}>
                  <i className="fas fa-paint-brush text-4xl mb-3"></i>
                  <h6 className="font-semibold text-white mb-1">
                    {logoImages.length >= 5
                      ? t('language') === 'fr' ? 'Limite atteinte' : 'Limit Reached'
                      : t('language') === 'fr' ? 'Déposer le logo ici' : 'Drop logo here'
                    }
                  </h6>
                  <p className="text-sm text-gray-400 mb-3">
                    {logoImages.length >= 5
                      ? t('language') === 'fr' 
                        ? 'Vous avez atteint la limite de 5 logos'
                        : 'You have reached the 5 logo limit'
                      : t('language') === 'fr' 
                        ? 'ou cliquez pour sélectionner'
                        : 'or click to select'
                    }
                  </p>
                  {logoImages.length < 5 && (
                    <div className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors">
                      {t('browseFiles')}
                    </div>
                  )}
                </div>
              </div>

              {/* File Info */}
              <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                <span>
                  {t('language') === 'fr' 
                    ? 'Formats supportés: PNG, JPEG, JPG, WebP'
                    : 'Supported formats: PNG, JPEG, JPG, WebP'
                  }
                </span>
                <span>
                  {t('language') === 'fr' ? 'Max 5 logos, 5MB chacun' : 'Max 5 logos, 5MB each'}
                </span>
              </div>

              {/* Preview for new logos */}
              {newLogoImagePreviews.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="fas fa-clock text-orange-400"></i>
                    <h6 className="text-sm text-orange-400 font-semibold">
                      {t('language') === 'fr' ? 'Prêt à télécharger' : 'Ready to Upload'}
                    </h6>
                    <span className="text-xs text-gray-400">
                      ({newLogoImagePreviews.length} {t('language') === 'fr' ? 'logo(s)' : 'logo(s)'})
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                    {newLogoImagePreviews.map((preview, index) => (
                      <div key={index} className="relative group bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-2">
                        <div className="aspect-square bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
                          <img
                            src={preview}
                            alt={`Logo Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Remove button */}
                        <button
                          onClick={() => handleRemoveNewLogoImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center transition-colors shadow-lg"
                          title={t('language') === 'fr' ? 'Supprimer' : 'Remove'}
                        >
                          <i className="fas fa-times text-xs"></i>
                        </button>
                        
                        {/* Image info */}
                        <div className="mt-2 text-center">
                          <p className="text-xs text-gray-400 truncate">
                            {newLogoImages[index]?.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {((newLogoImages[index]?.size || 0) / (1024 * 1024)).toFixed(1)}MB
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-xs text-gray-400 text-center mt-3">
                    {t('language') === 'fr' 
                      ? 'Ces logos seront ajoutés au début de votre galerie'
                      : 'These logos will be added to the beginning of your gallery'
                    }
                  </p>
                </div>
              )}

              {/* Upload Status */}
              {imageUploadStatus && (
                <div className={`mt-4 p-4 rounded-xl flex items-center ${
                  imageUploadStatus === 'success' 
                    ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30' :
                  imageUploadStatus === 'error' 
                    ? 'bg-gradient-to-r from-red-500/10 to-rose-500/10 border border-red-500/30' :
                    'bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30'
                }`}>
                  <div className="flex items-center flex-1">
                    {imageUploadStatus === 'uploading' && (
                      <i className="fas fa-spinner fa-spin text-blue-400 mr-3"></i>
                    )}
                    {imageUploadStatus === 'success' && (
                      <i className="fas fa-check-circle text-green-400 mr-3"></i>
                    )}
                    {imageUploadStatus === 'error' && (
                      <i className="fas fa-exclamation-triangle text-red-400 mr-3"></i>
                    )}
                    <div className="flex-1">
                      <span className={`text-sm font-medium ${
                        imageUploadStatus === 'success' ? 'text-green-400' :
                        imageUploadStatus === 'error' ? 'text-red-400' : 'text-blue-400'
                      }`}>
                        {imageUploadMessage}
                      </span>
                      
                      {/* Progress bar for uploads */}
                      {imageUploadStatus === 'uploading' && uploadProgress > 0 && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-400 mt-1 text-right">
                            {Math.round(uploadProgress)}%
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Logo Button */}
              {newLogoImages.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleUpdateLogoImage}
                    disabled={isUpdatingLogo || logoImages.length + newLogoImages.length > 5}
                    className={`px-6 py-3 font-semibold rounded-xl transition-all duration-300 flex items-center shadow-lg ${
                      isUpdatingLogo || logoImages.length + newLogoImages.length > 5
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white transform hover:scale-105'
                    }`}
                  >
                    {isUpdatingLogo ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        {t('language') === 'fr' ? 'Téléchargement...' : 'Uploading...'}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-cloud-upload-alt mr-2"></i>
                        {t('language') === 'fr' 
                          ? `Télécharger ${newLogoImages.length} logo(s)` 
                          : `Upload ${newLogoImages.length} Logo(s)`
                        }
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Current Images Gallery */}
          {imageViewMode === 'profile' && allImages.length > 0 && (
            <div key="profile-gallery" className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-lg font-semibold text-white">
                  {t('yourImages')}
                </h5>
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-600 px-3 py-1 rounded-full text-sm text-gray-300">
                    {allImages.length}/5
                  </div>
                  <div className="h-2 w-20 bg-gray-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
                      style={{ width: `${(allImages.length / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allImages.map((imageUrl, index) => (
                  <div key={index} className="relative group">
                    {/* Image Card */}
                    <div className={`relative bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-3 shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                      index === 0 ? 'ring-2 ring-purple-500 ring-opacity-50' : ''
                    }`}>
                      {/* Profile Badge */}
                      {index === 0 && (
                        <div className="absolute -top-2 -right-2 z-10">
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-3 py-1 rounded-full shadow-lg flex items-center">
                            <i className="fas fa-crown mr-1"></i>
                            {t('language') === 'fr' ? 'Profil' : 'Profile'}
                          </div>
                        </div>
                      )}
                      
                      {/* Image Container */}
                      <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-900">
                        <img
                          src={imageUrl}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 cursor-pointer"
                          onClick={() => handleImageClick(imageUrl)}
                        />
                        
                        {/* Top-left Controls */}
                        <div className="absolute top-2 left-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {index !== 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAsProfileImage(index);
                              }}
                              className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white w-8 h-8 rounded-full shadow-lg transform hover:scale-110 transition-all duration-200 flex items-center justify-center"
                              title={t('language') === 'fr' ? 'Définir comme image de profil' : 'Make profile picture'}
                            >
                              <i className="fas fa-star text-xs"></i>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(index);
                            }}
                            disabled={isRemovingImage === index || allImages.length <= 1}
                            className={`w-8 h-8 rounded-full shadow-lg transform hover:scale-110 transition-all duration-200 flex items-center justify-center ${
                              isRemovingImage === index || allImages.length <= 1
                                ? 'bg-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white'
                            }`}
                            title={t('language') === 'fr' ? 'Supprimer l\'image' : 'Remove image'}
                          >
                            {isRemovingImage === index ? (
                              <i className="fas fa-spinner fa-spin text-xs"></i>
                            ) : (
                              <i className="fas fa-trash-alt text-xs"></i>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Image Info */}
                      <div className="mt-3 px-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-300">
                            {t('language') === 'fr' ? 'Image' : 'Image'} #{index + 1}
                          </span>
                          {index === 0 && (
                            <span className="text-xs text-purple-400 font-medium">
                              {t('language') === 'fr' ? 'Principale' : 'Primary'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              
              </div>
            </div>
          )}

          {/* Current Logo Gallery */}
          {imageViewMode === 'logo' && logoImages.length > 0 && (
            <div key="logo-gallery" className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-lg font-semibold text-white">
                  {t('yourLogos')}
                </h5>
                <div className="flex items-center space-x-2">
                  <div className="bg-gray-600 px-3 py-1 rounded-full text-sm text-gray-300">
                    {logoImages.length}/5
                  </div>
                  <div className="h-2 w-20 bg-gray-600 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-300"
                      style={{ width: `${(logoImages.length / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {logoImages.map((imageUrl, index) => (
                  <div key={`logo-${index}`} className="relative group">
                    {/* Image Card */}
                    <div className={`relative bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl p-3 shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                      index === 0 ? 'ring-2 ring-green-500 ring-opacity-50' : ''
                    }`}>
                      {/* Logo Badge - Only show on first logo */}
                      {index === 0 && (
                        <div className="absolute -top-2 -right-2 z-10">
                          <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-3 py-1 rounded-full shadow-lg flex items-center">
                            <i className="fas fa-crown mr-1"></i>
                            {t('language') === 'fr' ? 'Logo Principal' : 'Main Logo'}
                          </div>
                        </div>
                      )}
                      
                      {/* Image Container */}
                      <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-900">
                        <img
                          src={imageUrl}
                          alt={`Logo ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110 cursor-pointer"
                          onClick={() => handleImageClick(imageUrl)}
                        />
                        
                        {/* Top-left Controls */}
                        <div className="absolute top-2 left-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {index !== 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAsMainLogo(index);
                              }}
                              className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white w-8 h-8 rounded-full shadow-lg transform hover:scale-110 transition-all duration-200 flex items-center justify-center"
                              title={t('language') === 'fr' ? 'Définir comme logo principal' : 'Make main logo'}
                            >
                              <i className="fas fa-star text-xs"></i>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeLogoImage(index);
                            }}
                            disabled={isRemovingLogo === index || logoImages.length <= 1}
                            className={`w-8 h-8 rounded-full shadow-lg transform hover:scale-110 transition-all duration-200 flex items-center justify-center ${
                              isRemovingLogo === index || logoImages.length <= 1
                                ? 'bg-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white'
                            }`}
                            title={t('language') === 'fr' ? 'Supprimer le logo' : 'Remove logo'}
                          >
                            {isRemovingLogo === index ? (
                              <i className="fas fa-spinner fa-spin text-xs"></i>
                            ) : (
                              <i className="fas fa-trash-alt text-xs"></i>
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Image Info */}
                      <div className="mt-3 px-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-300">
                            {t('language') === 'fr' ? 'Logo' : 'Logo'} #{index + 1}
                          </span>
                          {index === 0 && (
                            <span className="text-xs text-green-400 font-medium">
                              {t('language') === 'fr' ? 'Principal' : 'Primary'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          
        </div>

        {/* Profile Type Selector */}
        <div className="bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-700 mb-6">
          <h4 className="text-xl font-semibold text-white mb-4 text-center">{t('whatTypeCommunity')}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {['Streamer', 'Influencer', 'Discord', 'Website'].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handleSettingChange('information', 'profile', type)}
                className={`px-6 py-4 rounded-lg font-semibold transition-all duration-200 ${
                  settings.information.profile === type
                    ? 'bg-pink-600 text-white shadow-lg transform scale-105'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {t(`profile${type}`)}
              </button>
            ))}
          </div>
          
          {/* Update Profile Button */}
          <div className="flex justify-center">
            <button
              onClick={handleUpdateProfile}
              disabled={isUpdatingProfile}
              className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 ${
                isUpdatingProfile
                  ? 'bg-gray-600 cursor-not-allowed text-gray-400'
                  : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-green-500/50'
              }`}
            >
              {isUpdatingProfile ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  {t('language') === 'fr' ? 'Mise à jour...' : 'Updating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  {t('language') === 'fr' ? 'Mettre à jour le profil' : 'Update Profile'}
                </>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <i className="fas fa-user text-blue-400 mr-2"></i>
              {t('name')}
            </label>
            <input
              type="text"
              value={settings.information.name}
              onChange={(e) => handleSettingChange('information', 'name', e.target.value)}
              className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
              placeholder={t('namePlaceholder')}
            />
          </div>

          {/* Country Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <i className="fas fa-globe text-green-400 mr-2"></i>
              {t('country')}
            </label>
            <div className="relative location-dropdown">
              <button
                type="button"
                onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                className="w-full flex items-center justify-between p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors hover:bg-gray-600"
              >
                <div className="flex items-center">
                  {locationData.flagClass ? (
                    <span 
                      className={locationData.countryCode === 'W' ? 'mr-3' : `${locationData.flagClass} mr-3`}
                      style={{
                        fontSize: locationData.countryCode === 'W' ? '16px' : '16px',
                        width: '24px',
                        height: '16px',
                        borderRadius: '3px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      role="img" 
                      aria-label={`${locationData.country} flag`}
                    >
                      {locationData.countryCode === 'W' ? '🌍' : ''}
                    </span>
                  ) : (
                    <i className="fas fa-globe text-green-400 mr-3"></i>
                  )}
                  <span className="text-white">
                    {locationData.country || settings.information.country || (t('language') === 'fr' ? 'Sélectionner un pays' : 'Select a country')}
                  </span>
                  {locationData.countryCode && (
                    <span className="text-xs text-gray-400 ml-2">({locationData.countryCode})</span>
                  )}
                </div>
                <svg 
                  className={`ml-2 h-4 w-4 text-gray-400 transition-transform ${showLocationDropdown ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showLocationDropdown && (
                <div className="absolute top-full left-0 mt-2 w-full bg-gray-800 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto border border-gray-600">
                  <div className="p-3">
                    <div className="text-sm text-gray-400 px-3 py-2 font-medium">
                      {t('language') === 'fr' ? 'Sélectionnez votre pays' : 'Select your location'}
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('searchCountryPlaceholder')}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent mb-2"
                    />
                    {filteredCountries.length === 0 ? (
                      <div className="text-center py-4 text-gray-400">
                        {t('language') === 'fr' ? 'Aucun pays trouvé' : 'No country found'}
                      </div>
                    ) : (
                      filteredCountries.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => handleLocationSelect(country)}
                          className={`w-full flex items-center px-3 py-2 text-left hover:bg-gray-700 rounded transition-colors ${
                            locationData.countryCode === country.code ? 'bg-gray-700' : ''
                          }`}
                        >
                          <span 
                            className={country.code === 'W' ? 'mr-3' : `fi fi-${country.code.toLowerCase()} mr-3`}
                            style={{
                              fontSize: country.code === 'W' ? '16px' : '16px',
                              width: '24px',
                              height: '16px',
                              borderRadius: '3px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            role="img" 
                            aria-label={`${country.name} flag`}
                          >
                            {country.code === 'W' ? '🌍' : ''}
                          </span>
                          <span className="text-white text-sm">
                            {country.name}
                          </span>
                          <span className="text-xs text-gray-400 ml-auto">
                            {country.code}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Social Media Field with Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <i className="fas fa-share-alt text-purple-400 mr-2"></i>
              {t('socialMediaLabel')}
            </label>
            <div className="relative">
              <div className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 min-h-[48px] flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-transparent">
                {/* Display existing tags inside input */}
                {socialMediaTags.map((tag, index) => (
                  <div 
                    key={index}
                    className={`${tag.color} text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm`}
                  >
                    <span className="font-medium">{tag.platform} :</span>
                    <span className="font-semibold">{tag.useAt ? '@' : ''}{tag.username}</span>
                    <button
                      type="button"
                      onClick={() => removeSocialTag(index)}
                      className="ml-1 hover:bg-black/20 rounded-full w-4 h-4 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
                
                {/* Input field */}
                <input
                  type="text"
                  value={socialInput}
                  onChange={handleSocialInputChange}
                  onKeyDown={handleSocialKeyDown}
                  onFocus={() => socialInput.length > 0 && setShowSocialSuggestions(true)}
                  placeholder={socialMediaTags.length === 0 ? "@username" : ""}
                  className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-400 min-w-[200px]"
                />
              </div>
              
              {/* Platform suggestions dropdown */}
              {showSocialSuggestions && socialInput.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                  <div className="p-2">
                    <p className="text-xs text-gray-400 px-2 py-1">Select platform for @{socialInput.replace('@', '')}</p>
                    {socialPlatforms.map((platform, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => addSocialTag(platform)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded text-left transition-colors ${
                          index === selectedSocialIndex ? 'bg-gray-700' : 'hover:bg-gray-700'
                        }`}
                      >
                        <span className="text-2xl w-8 flex items-center justify-center">{platform.icon}</span>
                        <div className="flex-1">
                          <div className="text-white font-medium">{platform.name}</div>
                          <div className="text-xs text-gray-400">{platform.urlPattern}{socialInput.replace('@', '')}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {t('language') === 'fr' ? 
                'Tapez @username ou username et sélectionnez la plateforme' :
                'Type @username or username and select platform'
              }
            </p>
          </div>

          {/* Subscriber Count / Members Field - Conditional (NOT for Website) */}
          {settings.information.profile !== 'Website' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <i className="fas fa-users text-yellow-400 mr-2"></i>
                {settings.information.profile === 'Discord' ? t('numberOfMembers') : t('subscriberCountLabel')}
                {/* Only show optional for Discord */}
                {settings.information.profile === 'Discord' && (
                  <span className="text-gray-500 text-xs ml-1">{t('optional')}</span>
                )}
              </label>
              <input
                type="text"
                value={settings.information.subscriber}
                onChange={(e) => handleSettingChange('information', 'subscriber', e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                placeholder={settings.information.profile === 'Discord' ? t('enterMemberCount') : t('subscriberCountPlaceholder')}
              />
            </div>
          )}

          {/* URL / Discord Link Field - Conditional */}
          {settings.information.profile !== 'Streamer' && settings.information.profile !== 'Influencer' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <i className="fas fa-link text-cyan-400 mr-2"></i>
                {settings.information.profile === 'Discord' ? t('discordLink') : t('url')}
              </label>
              <input
                type="url"
                value={settings.information.url || ''}
                onChange={(e) => handleSettingChange('information', 'url', e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                placeholder={settings.information.profile === 'Discord' ? t('discordLinkPlaceholder') : t('urlPlaceholder')}
              />
            </div>
          )}

          {/* Member Counter - Only for Discord */}
          {settings.information.profile === 'Discord' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <i className="fas fa-users text-purple-400 mr-2"></i>
                {t('memberCounter')} <span className="text-gray-500 text-xs">{t('optional')}</span>
              </label>
              <input
                type="text"
                value={settings.information.member_counter || ''}
                onChange={(e) => handleSettingChange('information', 'member_counter', e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                placeholder={t('memberCounterPlaceholder')}
              />
            </div>
          )}

          {/* Monthly Traffic - Only for Website */}
          {settings.information.profile === 'Website' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <i className="fas fa-chart-line text-blue-400 mr-2"></i>
                {t('monthlyTraffic')} <span className="text-gray-500 text-xs">{t('optional')}</span>
              </label>
              <input
                type="text"
                value={settings.information.monthly_traffic || ''}
                onChange={(e) => handleSettingChange('information', 'monthly_traffic', e.target.value)}
                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                placeholder={t('enterMonthlyTraffic')}
              />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-300">
                <i className="fab fa-youtube text-red-500 mr-2"></i>
                {t('youtubeLinks')}
              </label>
              <button
                type="button"
                onClick={addYoutubeLink}
                className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('addYoutubeLink')}
              </button>
            </div>
            
            <div className="space-y-2">
              {settings.information.youtube.map((link, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => handleYoutubeChange(index, e.target.value)}
                    placeholder={t('youtubeUrlPlaceholder')}
                    className="flex-1 p-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                  />
                  {settings.information.youtube.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeYoutubeLink(index)}
                      className="bg-red-500 hover:bg-red-600 text-white p-3 rounded-lg transition-colors"
                      title={t('removeThisLink')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                <i className="fas fa-envelope text-green-500 mr-2"></i>
                {t('messageSection')}
              </label>
              <button
                type="button"
                onClick={() => openLanguageModal('message')}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors flex items-center"
              >
                <i className="fas fa-plus mr-1"></i>
                {t('addLanguage')}
              </button>
            </div>
            
            <RichTextEditor
              name="message"
              value={getLanguageValue(settings.information.message)}
              onChange={(e) => handleSettingChange('information', 'message', e.target.value)}
              placeholder={t('messagePlaceholder')}
              className=""
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                <i className="fas fa-user text-blue-500 mr-2"></i>
                {t('aboutSection')}
              </label>
              <button
                type="button"
                onClick={() => openLanguageModal('about')}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors flex items-center"
              >
                <i className="fas fa-plus mr-1"></i>
                {t('addLanguage')}
              </button>
            </div>
            
            <RichTextEditor
              name="about"
              value={getLanguageValue(settings.information.about)}
              onChange={(e) => handleSettingChange('information', 'about', e.target.value)}
              placeholder={t('aboutPlaceholder')}
              className=""
            />
          </div>
          
          {/* Services Section */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-4">
              <i className="fas fa-cogs text-purple-500 mr-2"></i>
              Services
            </label>
            
            {/* Services Grid */}
            <div className="mb-4">
              {services.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {services.map((service) => (
                    <div key={service.id} className="relative group">
                      <div className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-purple-500 transition-all duration-300">
                        {/* Delete Button */}
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        >
                          ×
                        </button>
                        
                        {/* Service Image - Click to Edit */}
                        <div 
                          className="aspect-square mb-3 rounded-lg overflow-hidden bg-gray-600 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => handleEditService(service)}
                          title="Click to edit service"
                        >
                          <img 
                            src={service.imagePreview} 
                            alt={service.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Service Title */}
                        <h4 className="text-sm font-medium text-gray-200 text-center truncate">
                          {service.title}
                        </h4>
                        
                        {/* Service URL */}
                        <p className="text-xs text-gray-400 text-center truncate mt-1">
                          {service.url}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <i className="fas fa-plus-circle text-4xl mb-2"></i>
                  <p>{t('noServicesAdded')}</p>
                </div>
              )}
              
              {/* Add Service Button */}
              <button
                onClick={handleAddService}
                disabled={services.length >= 4}
                className={`w-full py-3 px-4 rounded-lg border-2 border-dashed transition-all duration-300 ${
                  services.length >= 4 
                    ? 'border-gray-600 text-gray-500 cursor-not-allowed' 
                    : 'border-purple-500 text-purple-400 hover:border-purple-400 hover:text-purple-300 hover:bg-purple-500/10'
                }`}
              >
                <i className="fas fa-plus mr-2"></i>
                {services.length >= 4 
                  ? (t('language') === 'fr' ? 'Nombre maximum de services atteint (4/4)' : 'Maximum services reached (4/4)')
                  : `${t('addServiceButton')} (${services.length}/4)`
                }
              </button>
            </div>
          </div>
          
          
        </div>
        
        <div className="flex justify-end pt-4 border-t border-gray-700">
          <button
            onClick={handleUpdateInformation}
            disabled={isUpdatingInfo}
            className={`w-full px-6 py-2 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
              isUpdatingInfo 
                ? 'bg-gray-600 cursor-not-allowed' 
                : 'bg-pink-600 hover:bg-pink-700'
            }`}
          >
            {isUpdatingInfo ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {t('updating')}
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                {t('updateInformationButton')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderPasswordSettings = () => (
    <div className="p-8">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-2">{t('changePasswordTitle')}</h3>
        <p className="text-sm text-gray-400">{t('updatePasswordSecurity')}</p>
      </div>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <i className="fas fa-lock text-yellow-500 mr-2"></i>
              {t('currentPassword')}
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={settings.password.currentPassword}
                onChange={(e) => handleSettingChange('password', 'currentPassword', e.target.value)}
                className="w-full p-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                placeholder={t('currentPasswordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showCurrentPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <i className="fas fa-key text-blue-500 mr-2"></i>
              {t('newPassword')}
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={settings.password.newPassword}
                onChange={(e) => handleSettingChange('password', 'newPassword', e.target.value)}
                className="w-full p-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                placeholder={t('newPasswordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showNewPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <i className="fas fa-shield-alt text-green-500 mr-2"></i>
              {t('confirmNewPassword')}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={settings.password.confirmPassword}
                onChange={(e) => handleSettingChange('password', 'confirmPassword', e.target.value)}
                className="w-full p-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors"
                placeholder={t('confirmPasswordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {/* Password strength indicator */}
        {settings.password.newPassword && (
          <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
            <div className="text-sm font-medium text-gray-300 mb-2">{t('passwordStrength')}</div>
            <div className="flex space-x-1 mb-2">
              <div className={`h-2 flex-1 rounded ${
                settings.password.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-600'
              }`}></div>
              <div className={`h-2 flex-1 rounded ${
                /(?=.*[a-z])(?=.*[A-Z])/.test(settings.password.newPassword) ? 'bg-green-500' : 'bg-gray-600'
              }`}></div>
              <div className={`h-2 flex-1 rounded ${
                /(?=.*\d)/.test(settings.password.newPassword) ? 'bg-green-500' : 'bg-gray-600'
              }`}></div>
              <div className={`h-2 flex-1 rounded ${
                /(?=.*[!@#$%^&*])/.test(settings.password.newPassword) ? 'bg-green-500' : 'bg-gray-600'
              }`}></div>
            </div>
            <div className="text-xs text-gray-400">
              {t('passwordRequirements')}
            </div>
          </div>
        )}
        
        {/* Password match indicator */}
        {settings.password.confirmPassword && (
          <div className={`p-3 rounded-lg border ${
            settings.password.newPassword === settings.password.confirmPassword 
              ? 'bg-green-900 border-green-700' 
              : 'bg-red-900 border-red-700'
          }`}>
            <div className={`text-sm flex items-center ${
              settings.password.newPassword === settings.password.confirmPassword 
                ? 'text-green-400' 
                : 'text-red-400'
            }`}>
              {settings.password.newPassword === settings.password.confirmPassword 
                ? <><i className="fas fa-check-circle mr-2"></i> {t('passwordsMatch')}</> 
                : <><i className="fas fa-exclamation-triangle mr-2"></i> {t('passwordsDoNotMatch')}</>}
            </div>
          </div>
        )}
        
        <div className="flex justify-end pt-4 border-t border-gray-700">
          <button
            onClick={handleUpdatePassword}
            disabled={isUpdatingPassword}
            className={`px-6 py-2 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
              isUpdatingPassword 
                ? 'bg-gray-600 cursor-not-allowed' 
                : 'bg-pink-600 hover:bg-pink-700'
            }`}
          >
            {isUpdatingPassword ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                {t('updating')}
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                {t('updatePasswordButton')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Handle sending appeal for blocked account
  const handleSendAppeal = async () => {
    if (!appealMessage.trim()) {
      showToastMessage('Please enter an appeal message', 'error');
      return;
    }

    setIsSendingAppeal(true);
    try {
      const response = await fetch('/.netlify/functions/send-appeal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          streamerName: admin?.owner || 'Unknown',
          streamerEmail: admin?.email || '',
          appealMessage: appealMessage
        })
      });

      const result = await response.json();

      if (result.success) {
        showToastMessage('Appeal sent successfully', 'success');
        setShowAppealModal(false);
        setAppealMessage('');
      } else {
        showToastMessage('Failed to send appeal: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error sending appeal:', error);
      showToastMessage('Error sending appeal', 'error');
    } finally {
      setIsSendingAppeal(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">{t('loading')}</div>
      </div>
    );
  }

  if (!admin) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="h-screen bg-gray-900 text-white flex overflow-hidden">
      {/* Sidebar - Using reusable component */}
      <Sidebar 
        activeSidebarItem="settings"
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={closeMobileMenu}
        dashboardType="streamer"
      />

      {/* Mobile Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header - Fixed */}
        <div className="bg-gray-800 p-[14px] flex justify-between items-center flex-shrink-0 border-b border-gray-700">
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            <div>
              <h2 className="text-xl lg:text-2xl font-bold text-white">{t('settingsTitle')}</h2>
              <p className="text-sm text-gray-400">{t('manageInformationPreferences')}</p>
            </div>
          </div>

          {/* Account Status - Show if blocked */}
          {admin?.status === 'blocked' && (
            <div className="flex items-center space-x-3 bg-red-900/30 border border-red-500 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-red-200 font-medium text-sm">Account Blocked</span>
              </div>
              <button
                onClick={() => setShowAppealModal(true)}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-md transition-colors text-sm font-medium"
                title="Send appeal to unblock your account"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>Send Appeal</span>
              </button>
            </div>
          )}
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {/* Settings Tabs */}
          <div className="bg-gray-800 border-b border-gray-700">
            <div className="max-w-4xl mx-auto px-6">
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveSettingsTab('information')}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeSettingsTab === 'information' 
                      ? 'border-pink-500 text-pink-400' 
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <i className="fas fa-info-circle mr-2"></i>
                  {t('updateInformationTab')}
                </button>
                <button
                  onClick={() => setActiveSettingsTab('password')}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeSettingsTab === 'password' 
                      ? 'border-pink-500 text-pink-400' 
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  <i className="fas fa-lock mr-2"></i>
                  {t('changePasswordTab')}
                </button>
              </div>
            </div>
          </div>

          {/* Settings Content */}
          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
              {activeSettingsTab === 'information' && renderInformationSettings()}
              {activeSettingsTab === 'password' && renderPasswordSettings()}
            </div>
          </div>
        </div>
      </div>

      {/* Add Service Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <i className={`${editingService ? 'fas fa-edit text-blue-500' : 'fas fa-plus-circle text-purple-500'} mr-2`}></i>
                {editingService ? 'Edit Service' : 'Add New Service'}
              </h3>
              <button
                onClick={closeServiceModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Service URL */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fas fa-link text-green-500 mr-2"></i>
                  Service URL
                </label>
                <input
                  type="url"
                  value={newService.url}
                  onChange={(e) => setNewService(prev => ({ ...prev, url: e.target.value }))}
                  placeholder={t('serviceUrlPlaceholder')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              {/* Service Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fas fa-heading text-blue-500 mr-2"></i>
                  Service Title
                </label>
                <input
                  type="text"
                  value={newService.title}
                  onChange={(e) => setNewService(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('serviceCaptionPlaceholder')}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>


              {/* Service Image */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fas fa-image text-yellow-500 mr-2"></i>
                  Service Image
                </label>
                
                {/* Image Preview */}
                {newService.imagePreview ? (
                  <div className="mb-3">
                    <div className="w-32 h-32 mx-auto rounded-lg overflow-hidden border-2 border-purple-500">
                      <img 
                        src={newService.imagePreview} 
                        alt="Service preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      onClick={() => setNewService(prev => ({ ...prev, image: null, imagePreview: null }))}
                      className="block mx-auto mt-2 text-sm text-red-400 hover:text-red-300 transition-colors"
                    >
                      <i className="fas fa-trash mr-1"></i>
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
                    <i className="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-2"></i>
                    <p className="text-gray-400 mb-2">Click to upload service image</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleServiceImageChange}
                      className="hidden"
                      id="service-image-upload"
                    />
                    <label
                      htmlFor="service-image-upload"
                      className="inline-block px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg cursor-pointer transition-colors"
                    >
                      Choose Image
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700">
              <button
                onClick={closeServiceModal}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveService}
                disabled={!newService.title.trim() || !newService.url.trim() || (!newService.image && !newService.cloudinaryUrl) || newService.isUploading}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center"
              >
                {newService.isUploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    {editingService ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <i className={`${editingService ? 'fas fa-save' : 'fas fa-plus'} mr-2`}></i>
                    {editingService ? 'Update Service' : 'Add Service'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white flex items-center">
                <i className="fas fa-language text-blue-500 mr-2"></i>
                {hasExistingLanguageContent() ? 'Update' : 'Add'} {languageModalType.charAt(0).toUpperCase() + languageModalType.slice(1)} Language
              </h3>
              <button
                onClick={closeLanguageModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Country Dropdown with Flags */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Country
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      {languageForm.country ? (
                        <>
                          <span className={`fi fi-${languageForm.country.toLowerCase()} mr-2`}></span>
                          <span>{countries.find(c => c.code === languageForm.country)?.name}</span>
                        </>
                      ) : (
                        <span className="text-gray-400">Select a Country</span>
                      )}
                    </div>
                    <i className={`fas fa-chevron-${showCountryDropdown ? 'up' : 'down'} text-gray-400`}></i>
                  </button>
                  
                  {showCountryDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {countries.filter(country => ['FR', 'US'].includes(country.code)).map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => selectCountry(country.code)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-600 flex items-center transition-colors"
                        >
                          <span className={`fi fi-${country.code.toLowerCase()} mr-3`}></span>
                          <span className="text-white">{country.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Text Field */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {languageModalType.charAt(0).toUpperCase() + languageModalType.slice(1)}
                </label>
                <RichTextEditor
                  name={`${languageModalType}Language`}
                  value={languageForm.text}
                  onChange={(e) => setLanguageForm(prev => ({ ...prev, text: e.target.value }))}
                  placeholder={`Enter ${languageModalType} in the selected language...`}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
              <button
                onClick={closeLanguageModal}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLanguageSubmit}
                disabled={!languageForm.country.trim() || !languageForm.text.trim()}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center"
              >
                <i className={`fas fa-${hasExistingLanguageContent() ? 'edit' : 'plus'} mr-2`}></i>
                {hasExistingLanguageContent() ? 'Update Language' : 'Add Language'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
          toastType === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          <div className="flex items-center space-x-2">
            {toastType === 'success' ? (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="text-white font-medium">{toastMessage}</span>
          </div>
        </div>
      )}

      {/* Image Popup Modal */}
      {showImageModal && selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 z-10 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image */}
            <img 
              src={selectedImage} 
              alt="Profile upload" 
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Appeal Modal */}
      {showAppealModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-lg w-full p-6">
            <h3 className="text-2xl font-bold text-white mb-4">Send Appeal</h3>
            <p className="text-gray-300 mb-4">
              Explain why your account should be unblocked. Your appeal will be reviewed by the administration team.
            </p>
            <textarea
              value={appealMessage}
              onChange={(e) => setAppealMessage(e.target.value)}
              placeholder="Enter your appeal message..."
              className="w-full bg-gray-700 text-white rounded-lg p-3 mb-4 h-40 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowAppealModal(false);
                  setAppealMessage('');
                }}
                disabled={isSendingAppeal}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendAppeal}
                disabled={isSendingAppeal || !appealMessage.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {isSendingAppeal ? 'Sending...' : 'Send Appeal'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Settings;